import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import toast from 'react-hot-toast';
import { apiService } from '../../services/apiService';
import { useBrandingColors } from '../../hooks/useBrandingColors';
import { PositionStatusEnum, PositionLevelEnum } from '../../../generated-src/client/positions/api';
import { levelMap, levelMapReverse, UI_LEVEL_FALLBACK, API_LEVEL_FALLBACK, mapApiLevelToUi, mapLevelToMinScore } from '../../utils/vacancies/mapping';
import { RichTextEditor } from './RichTextEditor';
import IndustryModal from '../ui/IndustryModal';
import SpecializationModal from '../ui/SpecializationModal';
import AreaModal from '../ui/AreaModal';
import SearchMultiSelectModal from '../ui/SearchMultiSelectModal';
import { getHHActiveVacancies, getHHVacancyDetail, getAvitoVacancyDetail, exportVacancyToHH, updateHHVacancyExport, getSources, getHHEmployerAddresses, type HHEmployerAddress } from '../../relevanter/services/api';
import AtsVacancySelect from '../../relevanter/components/AtsVacancySelect';
import { buildAvailableAtsSourceLinkOptions, getValidAtsSourceLinkSource, type AtsSourceLinkOption, type AtsSourceLinkSource } from './sourceLinkSources';
import { ContentTabToggle } from '../ui/content-tab-toggle';
import { InterviewQuestionsEditForm, InterviewQuestionsEditFormHandle } from './InterviewQuestionsEditForm';
import { getIndustryNameById, getStoredSpecializationName, findSpecializationPathKeyById, findIndustryIdByName, findSpecializationPathKeyByName, getAreaNameById, getAreaIdByName, compressCityIds, fetchHHMetroCities, fetchHHCountries, fetchHHWorkSchedules, fetchHHCurrencies, isSpecializationLeafId, isAreaLeafId, findCountryIdByName, type MetroCityOption, type CountryOption, type CurrencyOption } from '../../services/hhDictionariesService';
import { getAvitoStatus } from '../../services/avitoService';
import {
  buildHhAreaFallbackSubtitle,
  DEFAULT_HH_AREA_FALLBACK_SUBTITLE,
  HH_AREA_MAPPING_ERROR_SUBTITLE,
} from './avitoLocationFallback';
import {
  fetchAvitoLocations,
  fetchAvitoResumeSpecializations,
  getAvitoLocationNameById,
  getAvitoResumeSpecializationNameById,
  compressAvitoLocationIds,
  resolveAvitoLocationsToHH,
} from '../../services/avitoDictionariesService';
import { isSameHhStoredSpecializationSelection } from '../../sources/hh/specializationSelection';
import { SearchableMetroSelect } from '../ui/searchable-metro-select';
import { formatNumberWithSpaces, parseFormattedNumber } from '../../utils/numberFormat';
import { getExportDescriptionLength } from '../../relevanter/services/api';
import { extractLastPathSegment } from '../../utils/filterUtils';
import {
  CANDIDATE_STATUSES,
  EMPLOYMENT_CONTRACT_TYPES,
  WORK_FORMATS,
  WORK_SCHEDULES,
  GENDER_OPTIONS,
  EXPERIENCE_OPTIONS,
  EXPERIENCE_PERIOD_OPTIONS,
  EDUCATION_OPTIONS,
} from '../../constants/vacancyFormConstants';
import { useBrandingStore } from '../../store/brandingStore';
import { ChevronDownIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ReactComponent as AiScreeningActionIcon } from '../../assets/icons/icon-action-ai-screening.svg';
import { ReactComponent as HrInterviewActionIcon } from '../../assets/icons/icon-action-hr-interview.svg';
import { ReactComponent as InfoCircleIcon } from '../../assets/icons/info-circle.svg';
import aiAssistantIcon from '../../assets/icons/ai-assistant-icon.png';
import aiPanelExpandIcon from '../../assets/icons/ai-panel-expand.png';
import { RequirementsPanel } from './ai-assistant/RequirementsPanel';
import type { RequirementItem } from './ai-assistant/types';
import MailingsPage from '../../pages/MailingsPage';
import { ExcludeJumpersCheckbox } from './ExcludeJumpersCheckbox';
import {
  getInternalVacancyTitleLength,
  getHhVacancyTitleLength,
  HH_VACANCY_TITLE_MAX_LENGTH,
  VACANCY_TITLE_MAX_LENGTH,
  normalizeHhVacancyTitle,
  normalizeInternalVacancyTitle,
  validateHhVacancyExport,
  validateInternalVacancyStringLimits,
  validateInternalVacancyTitle,
} from '../../constants/vacancyConstants';

// Dropdown components for HH vacancies
interface HHVacancy {
  id: string;
  name: string;
  area?: { name: string };
}

type MetroEntry = {
  stationId: string;
  address: string;
  hhAddressId?: string;
};

type AvitoLinkedVacancy = Record<string, any>;

const createAvitoLinkedVacancyPlaceholder = (vacancyId: string): AvitoLinkedVacancy => ({
  avitoVacancyId: vacancyId,
  title: '',
  __placeholder: true,
});

// Single-select dropdown for main vacancy
interface HHMainVacancyDropdownProps {
  vacancies: HHVacancy[];
  selectedId: string | null;
  onChange: (id: string | null) => void;
  disabled?: boolean;
  loading?: boolean;
}

function HHMainVacancyDropdown({
  vacancies,
  selectedId,
  onChange,
  disabled,
  loading
}: HHMainVacancyDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectVacancy = (id: string | null) => {
    onChange(id);
    setIsOpen(false);
  };

  // Если сохранённая hh-вакансия больше не в списке активных (архивирована/удалена) —
  // автоматически снимаем привязку. Окончательная очистка в БД идёт через scheduled worker
  // на бэке; здесь — мгновенный отклик UI.
  useEffect(() => {
    if (loading || !selectedId || vacancies.length === 0) return;
    const stillActive = vacancies.some(v => v.id === selectedId);
    if (!stillActive) {
      onChange(null);
    }
  }, [loading, selectedId, vacancies, onChange]);

  const getDisplayText = () => {
    if (loading) return 'Загрузка...';
    if (!selectedId) return 'Выберите вакансию...';
    const vacancy = vacancies.find(v => v.id === selectedId);
    return vacancy ? vacancy.name : selectedId;
  };

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="w-full px-6 py-4 border border-[#e2e4e9] rounded-xl text-base focus:outline-none disabled:bg-gray-100 bg-white text-left flex items-center justify-between"
      >
        <span className={`min-w-0 truncate ${!selectedId ? 'text-gray-400' : 'text-[#0a0d14]'}`}>
          {getDisplayText()}
        </span>
        <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-[#e2e4e9] rounded-xl shadow-lg max-h-60 overflow-y-auto">
          <button
            type="button"
            onClick={() => selectVacancy(null)}
            className={`w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 ${
              !selectedId ? 'bg-gray-50' : ''
            }`}
          >
            <span className="text-sm text-gray-400">Не выбрана</span>
          </button>
          {vacancies.map((vacancy) => {
            const isSelected = selectedId === vacancy.id;
            return (
              <button
                key={vacancy.id}
                type="button"
                onClick={() => selectVacancy(vacancy.id)}
                className={`w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${
                  isSelected ? 'bg-gray-50' : ''
                }`}
              >
                <span className="text-sm text-[#0a0d14]">{vacancy.name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Multi-select dropdown for additional vacancies
interface HHAdditionalVacanciesDropdownProps {
  vacancies: HHVacancy[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  loading?: boolean;
}

function HHAdditionalVacanciesDropdown({
  vacancies,
  selectedIds,
  onChange,
  disabled,
  loading
}: HHAdditionalVacanciesDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleVacancy = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter(v => v !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  // Автоматически убираем из выбора hh-вакансии, которых больше нет в списке активных.
  useEffect(() => {
    if (loading || selectedIds.length === 0 || vacancies.length === 0) return;
    const activeIds = new Set(vacancies.map(v => v.id));
    const filtered = selectedIds.filter(id => activeIds.has(id));
    if (filtered.length !== selectedIds.length) {
      onChange(filtered);
    }
  }, [loading, selectedIds, vacancies, onChange]);

  const visibleSelectedIds = selectedIds.filter(id => vacancies.some(v => v.id === id));

  const getDisplayText = () => {
    if (loading) return 'Загрузка...';
    if (visibleSelectedIds.length === 0) return 'Выберите вакансии...';
    if (visibleSelectedIds.length === 1) {
      const vacancy = vacancies.find(v => v.id === visibleSelectedIds[0]);
      return vacancy ? vacancy.name : 'Выберите вакансии...';
    }
    return `Выбрано: ${visibleSelectedIds.length}`;
  };

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="w-full px-6 py-4 border border-[#e2e4e9] rounded-xl text-base focus:outline-none disabled:bg-gray-100 bg-white text-left flex items-center justify-between"
      >
        <span className={selectedIds.length === 0 ? 'text-gray-400' : 'text-[#0a0d14]'}>
          {getDisplayText()}
        </span>
        <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-[#e2e4e9] rounded-xl shadow-lg max-h-60 overflow-y-auto">
          {vacancies.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500">Нет доступных вакансий</div>
          ) : (
            vacancies.map((vacancy) => {
              const isSelected = selectedIds.includes(vacancy.id);
              return (
                <button
                  key={vacancy.id}
                  type="button"
                  onClick={() => toggleVacancy(vacancy.id)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 border-b border-gray-100 last:border-b-0"
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'bg-[#e16349] border-[#e16349]' : 'border-gray-300'
                  }`}>
                    {isSelected && <CheckIcon className="w-3 h-3 text-white" />}
                  </div>
                  <span className="text-sm text-[#0a0d14] truncate">{vacancy.name}</span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

type VacancyEditorTab = 'vacancy' | 'dialogRequirements' | 'interview';

const shouldOpenMailingsTabFromUrl = (): boolean => {
  try {
    return new URLSearchParams(window.location.search).get('tab') === 'mailings';
  } catch {
    return false;
  }
};

const getInitialVacancyEditorTab = (): VacancyEditorTab => (
  shouldOpenMailingsTabFromUrl() ? 'dialogRequirements' : 'vacancy'
);


interface VacancyDataEditFormProps {
  vacancyId?: number;
  onBack: () => void;
  onSaveSuccess?: (vacancyId: number) => void;
  initialAiPrompt?: string;
  generatedData?: any;
  aiRequirements?: { required: string[]; optional: string[]; forDialog?: string[] };
  aiRequirementsWithWeights?: { required: RequirementItem[]; optional: RequirementItem[] };
  onBeforeCreate?: (formData: any) => Promise<void>;
  requirementsReviewed?: boolean;
  onRequirementsReviewed?: () => void;
  hhImportVacancyId?: string | null;
  /** Pre-select the "Основная вакансия" dropdown with this HH vacancy id WITHOUT running
   * the full form-fields import. Используется только когда пользователь выбирает вакансию
   * из СВОЕГО СПИСКА опубликованных HH-вакансий в AI-ассистенте — такая вакансия уже
   * загружена в hhVacancies и у дропдауна есть её имя. При импорте по URL этот prop
   * не устанавливается (не линкуем чужую вакансию автоматически). */
  initialHhVacancyLink?: string | null;
  /** Вызывается при каждом изменении ключевых полей формы, чтобы родитель (AI чат) мог
   * использовать АКТУАЛЬНОЕ состояние формы как currentData при генерации, а не только
   * последний AI-ответ. Без этого ручные правки пользователя (например, "тест" в о
   * компании) не видны backend'у и могут быть перезаписаны при повторной генерации. */
  onFormDataChange?: (data: Record<string, any>) => void;
  showCollapsedAiSidebar?: boolean;
  onExpandAiPanel?: () => void;
  rightPanel?: React.ReactNode;
}

interface SkillsInputField {
  id: string;
  type: 'any' | 'all';
  skills: string[];
  inputValue: string;
  skillExperience?: Record<string, string>;
  groupExperience?: string;
}

export function VacancyDataEditForm({ vacancyId, onBack, onSaveSuccess, initialAiPrompt, generatedData: externalGeneratedData, showCollapsedAiSidebar, onExpandAiPanel, rightPanel, aiRequirements, aiRequirementsWithWeights, onBeforeCreate, hhImportVacancyId, initialHhVacancyLink, onFormDataChange, requirementsReviewed = false, onRequirementsReviewed }: VacancyDataEditFormProps) {
  const colors = useBrandingColors();
  const brandingData = useBrandingStore(state => state.data);

  const [activeTab, setActiveTab] = useState<VacancyEditorTab>(getInitialVacancyEditorTab);
  const openMailingsTabFromUrl = shouldOpenMailingsTabFromUrl();
  const mailingsSaveRef = useRef<(() => Promise<boolean>) | null>(null);
  const mailingsStartRef = useRef<(() => Promise<void>) | null>(null);
  const mailingsStopRef = useRef<(() => Promise<void>) | null>(null);
  const [isMailingStartAvailable, setIsMailingStartAvailable] = useState(false);
  const [isMailingStopAvailable, setIsMailingStopAvailable] = useState(false);

  useEffect(() => {
    if (openMailingsTabFromUrl) {
      setActiveTab('dialogRequirements');
    }
  }, [openMailingsTabFromUrl]);

  // Internal requirements state (for edit mode — loaded from vacancy data)
  const [internalRequirements, setInternalRequirements] = useState<{
    required: RequirementItem[];
    optional: RequirementItem[];
  } | null>(null);
  const [showInternalReqPanel, setShowInternalReqPanel] = useState(false);
  // Cache the full aiRequirementsWeighted object on load so save preserves for_dialog
  // (edited on the mailing settings page). If empty/missing, first save seeds for_dialog.
  const loadedAiRequirementsFullRef = useRef<any>(null);
  const requirementsEditedInVacancyFormRef = useRef(false);

  // Existing form state
  const [jobTitle, setJobTitle] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [skillsInputs, setSkillsInputs] = useState<SkillsInputField[]>([
    { id: '1', type: 'any', skills: [], inputValue: '', skillExperience: {}, groupExperience: '' }
  ]);
  const [minScore, setMinScore] = useState(5);
  const [timePerAnswer, setTimePerAnswer] = useState('2 мин');
  const [level, setLevel] = useState<string>('Middle');
  const [questionCount, setQuestionCount] = useState(5);
  const [status, setStatus] = useState<PositionStatusEnum>(PositionStatusEnum.ACTIVE);

  const [industry, setIndustry] = useState<string[]>([]);
  const [specialization, setSpecialization] = useState<string[]>([]);
  const [avitoSpecialization, setAvitoSpecialization] = useState<string[]>([]);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedCityIds, setSelectedCityIds] = useState<string[]>([]);
  const [selectedAvitoLocationIds, setSelectedAvitoLocationIds] = useState<string[]>([]);
  const [strictRegionSearch, setStrictRegionSearch] = useState(false);
  const [candidateStatuses, setCandidateStatuses] = useState<string[]>([]);
  const [experienceInPosition, setExperienceInPosition] = useState<string[]>([]);
  const [experiencePeriod, setExperiencePeriod] = useState('');
  const [totalExperience, setTotalExperience] = useState<string[]>([]);
  const [excludeJumpers, setExcludeJumpers] = useState(false);
  const [filterByExpIndustry, setFilterByExpIndustry] = useState(false);
  const [salaryFrom, setSalaryFrom] = useState<string>('');
  const [salaryTo, setSalaryTo] = useState<string>('');
  const [education, setEducation] = useState<string[]>([]);
  const [ageFrom, setAgeFrom] = useState<string>('');
  const [ageTo, setAgeTo] = useState<string>('');
  const [includeNoAge, setIncludeNoAge] = useState(false);
  const [gender, setGender] = useState('any');
  const [citizenship, setCitizenship] = useState<string[]>([]);
  const [companyDescription, setCompanyDescription] = useState('');
  const [workFormat, setWorkFormat] = useState('');
  const [workSchedule, setWorkSchedule] = useState('');
  const [officeCityId, setOfficeCityId] = useState('');
  const [officeCityName, setOfficeCityName] = useState('');
  const [isOfficeCityModalOpen, setIsOfficeCityModalOpen] = useState(false);
  const [hhWorkSchedules, setHhWorkSchedules] = useState<Array<{id: string; name: string}>>([]);
  const [metroCities, setMetroCities] = useState<MetroCityOption[]>([]);
  const [metroEntries, setMetroEntries] = useState<MetroEntry[]>([{stationId: '', address: ''}]);
  const [hhEmployerAddresses, setHhEmployerAddresses] = useState<HHEmployerAddress[]>([]);
  const [hhEmployerAddressesLoading, setHhEmployerAddressesLoading] = useState(false);
  const [useMetroInSearch, setUseMetroInSearch] = useState(false);
  const [employmentContractTypes, setEmploymentContractTypes] = useState<string[]>([]);
  const [socialPackageText, setSocialPackageText] = useState<string>('');
  const [relocationLocation, setRelocationLocation] = useState('');
  const [relocationAssistance, setRelocationAssistance] = useState('');
  const [hasNda, setHasNda] = useState(false);
  const [isAiScreeningEnabled, setIsAiScreeningEnabled] = useState(false);
  const [isHrInterviewEnabled, setIsHrInterviewEnabled] = useState(true);
  const [isScoringEnabled, setIsScoringEnabled] = useState(true);
  const [hasBonuses, setHasBonuses] = useState(false);
  const [salarySpecified, setSalarySpecified] = useState(false);
  const [salaryGross, setSalaryGross] = useState<boolean | null>(null);
  const [salaryFrequency, setSalaryFrequency] = useState<string>('');
  const [salaryCurrency, setSalaryCurrency] = useState<string>('RUR');
  const [hhCurrencies, setHhCurrencies] = useState<CurrencyOption[]>([]);
  const [workingHours, setWorkingHours] = useState<string>('');
  const [showInternalTagInput, setShowInternalTagInput] = useState(false);
  const [internalTag, setInternalTag] = useState('');

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isInterviewSaving, setIsInterviewSaving] = useState(false);
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  const interviewFormRef = useRef<InterviewQuestionsEditFormHandle | null>(null);
  const [isInterviewFormReady, setIsInterviewFormReady] = useState(!vacancyId);
  const formContainerRef = useRef<HTMLDivElement | null>(null);
  // Состояние анимации закрытия: при клике "Отмена" плавно исчезаем, затем вызываем onBack.
  const [isClosing, setIsClosing] = useState(false);
  const handleCancelClick = () => {
    if (isClosing) return;
    setIsClosing(true);
    setTimeout(() => onBack(), 220);
  };
  const handleMailingStart = async () => {
    if (!mailingsStartRef.current || isSaving || isInterviewSaving || isClosing) return;
    await mailingsStartRef.current();
  };
  const handleMailingStop = async () => {
    if (!mailingsStopRef.current || isSaving || isInterviewSaving || isClosing) return;
    await mailingsStopRef.current();
  };
  const flexContainerRef = useRef<HTMLDivElement | null>(null);
  const aiPanelRef = useRef<HTMLDivElement | null>(null);
  const [aiPanelOffset, setAiPanelOffset] = useState(200);

  useEffect(() => {
    setIsInterviewFormReady(!vacancyId);
  }, [vacancyId]);

  // Push актуальное состояние формы в родителя на каждое изменение key-полей.
  // AI-чат должен видеть ручные правки пользователя (а не последний AI-ответ) при
  // следующем вызове chatGenerateVacancy, чтобы бэкенд корректно защищал их от
  // перезаписи в parseChatResponse.
  useEffect(() => {
    if (!onFormDataChange) return;
    const data: Record<string, any> = {};
    if (jobTitle) data.title = jobTitle;
    if (jobDescription) data.jobDescription = jobDescription;
    if (companyDescription) data.companyDescription = companyDescription;
    if (socialPackageText) data.socialPackageText = socialPackageText;
    if (workFormat) data.workFormat = workFormat;
    if (workSchedule) data.workSchedule = workSchedule;
    if (officeCityName) data.officeCity = officeCityName;
    const salaryFromNum = salaryFrom ? Number(String(salaryFrom).replace(/\s+/g, '')) : NaN;
    if (Number.isFinite(salaryFromNum) && salaryFromNum > 0) data.salaryFrom = salaryFromNum;
    const salaryToNum = salaryTo ? Number(String(salaryTo).replace(/\s+/g, '')) : NaN;
    if (Number.isFinite(salaryToNum) && salaryToNum > 0) data.salaryTo = salaryToNum;
    if (level) data.level = level;
    if (experienceInPosition?.length) data.experienceInPosition = experienceInPosition;
    if (totalExperience?.length) data.totalExperience = totalExperience;
    const allSkills = skillsInputs.flatMap(g => g.skills || []);
    if (allSkills.length > 0) data.skills = allSkills;
    onFormDataChange(data);
  }, [
    jobTitle, jobDescription, companyDescription, socialPackageText,
    workFormat, workSchedule, officeCityName,
    salaryFrom, salaryTo,
    level, experienceInPosition, totalExperience, skillsInputs,
    onFormDataChange,
  ]);

  // Dynamically measure the offset from viewport top to the flex container
  useEffect(() => {
    const measure = () => {
      if (flexContainerRef.current) {
        const rect = flexContainerRef.current.getBoundingClientRect();
        const offset = Math.round(rect.top) + 32;
        // Guard against bad measurements during remount (rect.top near 0)
        if (offset > 100) {
          setAiPanelOffset(offset);
        }
      }
    };
    // Delay initial measurement to ensure layout is settled after remount
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(measure);
    });
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', measure);
    };
  }, []);

  const setupDynamicPanelHeight = useCallback((el: HTMLElement) => {
    const findScrollParent = (node: HTMLElement | null): HTMLElement | null => {
      while (node && node !== document.documentElement) {
        const style = window.getComputedStyle(node);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') return node;
        node = node.parentElement;
      }
      return null;
    };
    const scrollContainer = findScrollParent(el);

    const updatePanelHeight = () => {
      const elRect = el.getBoundingClientRect();
      const top = Math.max(elRect.top, 72);
      const height = window.innerHeight - top - 20;
      el.style.height = `${Math.max(height, 200)}px`;
    };
    updatePanelHeight();
    if (scrollContainer) scrollContainer.addEventListener('scroll', updatePanelHeight);
    window.addEventListener('resize', updatePanelHeight);
    return () => {
      if (scrollContainer) scrollContainer.removeEventListener('scroll', updatePanelHeight);
      window.removeEventListener('resize', updatePanelHeight);
    };
  }, []);

  useEffect(() => {
    const el = aiPanelRef.current;
    if (!el) return;
    return setupDynamicPanelHeight(el);
  }, [setupDynamicPanelHeight]);

  const reqPanelCleanupRef = useRef<(() => void) | null>(null);
  const reqPanelCallbackRef = useCallback((el: HTMLDivElement | null) => {
    if (reqPanelCleanupRef.current) {
      reqPanelCleanupRef.current();
      reqPanelCleanupRef.current = null;
    }
    if (el) {
      reqPanelCleanupRef.current = setupDynamicPanelHeight(el);
    }
  }, [setupDynamicPanelHeight]);

  const reqBookmarkCleanupRef = useRef<(() => void) | null>(null);
  const reqBookmarkCallbackRef = useCallback((el: HTMLDivElement | null) => {
    if (reqBookmarkCleanupRef.current) {
      reqBookmarkCleanupRef.current();
      reqBookmarkCleanupRef.current = null;
    }
    if (el) {
      reqBookmarkCleanupRef.current = setupDynamicPanelHeight(el);
    }
  }, [setupDynamicPanelHeight]);

  // Длина описания для HH (подсчёт на сервере — единственная точка истины)
  const [hhDescriptionInfo, setHhDescriptionInfo] = useState<{
    htmlLength: number;
    plainLength: number;
    minLength: number;
    maxLength: number;
    warnings?: string[];
    blocks?: {
      description: { length: number };
      companyDescription: { length: number };
      socialPackageText: { length: number };
    };
  }>({ htmlLength: 0, plainLength: 0, minLength: 150, maxLength: 10000 });

  useEffect(() => {
    const timer = setTimeout(() => {
      getExportDescriptionLength({
        companyDescription: companyDescription || '',
        description: jobDescription || '',
        socialPackageText: socialPackageText || '',
      })
        .then(setHhDescriptionInfo)
        .catch(() => {}); // при ошибке сети — оставляем предыдущее значение
    }, 300);
    return () => clearTimeout(timer);
  }, [companyDescription, jobDescription, socialPackageText]);

  // Validation errors state
  const [validationErrors, setValidationErrors] = useState<{
    jobTitle?: boolean;
    jobDescription?: boolean;
    companyDescription?: boolean;
    socialPackageText?: boolean;
    salary?: boolean;
  }>({});

  // Modal states
  const [isIndustryModalOpen, setIsIndustryModalOpen] = useState(false);
  const [isSpecializationModalOpen, setIsSpecializationModalOpen] = useState(false);
  const [isAvitoSpecializationModalOpen, setIsAvitoSpecializationModalOpen] = useState(false);
  const [isAreaModalOpen, setIsAreaModalOpen] = useState(false);
  const [isHhAreaFallbackModalOpen, setIsHhAreaFallbackModalOpen] = useState(false);
  const [hhAreaFallbackSubtitle, setHhAreaFallbackSubtitle] = useState(DEFAULT_HH_AREA_FALLBACK_SUBTITLE);
  const [isCitizenshipModalOpen, setIsCitizenshipModalOpen] = useState(false);

  // HH.ru vacancy linking state
  const [hhVacancyId, setHhVacancyId] = useState<string | null>(null);
  const [hhAdditionalVacancyIds, setHhAdditionalVacancyIds] = useState<string[]>([]);
  const [hhVacancies, setHhVacancies] = useState<Array<{ id: string; name: string; area?: { name: string } }>>([]);
  const [hhVacanciesLoading, setHhVacanciesLoading] = useState(false);
  const [hhImportLoading, setHhImportLoading] = useState(false);
  const [hhExportLoading, setHhExportLoading] = useState(false);

  // Экспорт-флоу: модалки валидации и подтверждения
  const [isExportSpecModalOpen, setIsExportSpecModalOpen] = useState(false);
  const [isExportAreaModalOpen, setIsExportAreaModalOpen] = useState(false);
  const [isExportConfirmOpen, setIsExportConfirmOpen] = useState(false);
  const [exportSpecOverride, setExportSpecOverride] = useState<string | null>(null);
  const [exportCityOverride, setExportCityOverride] = useState<string | null>(null);

  // FR-E2E-6: Привязки вакансий из любых источников
  const [sourceLinks, setSourceLinks] = useState<Array<{ id: number; source: string; externalVacancyId: string; createdAt: string }>>([]);
  const [sourceLinkSource, setSourceLinkSource] = useState<AtsSourceLinkSource | ''>('');
  const [sourceLinkVacancyId, setSourceLinkVacancyId] = useState('');
  const [sourceLinkVacancyTitle, setSourceLinkVacancyTitle] = useState('');
  const [sourceLinkLoading, setSourceLinkLoading] = useState(false);
  // Локальные привязки до создания вакансии (режим создания)
  const [pendingSourceLinks, setPendingSourceLinks] = useState<Array<{ tempId: number; source: string; externalVacancyId: string }>>([]);
  const [atsSourceOptions, setAtsSourceOptions] = useState<AtsSourceLinkOption[]>([]);
  const [hasAvitoIntegration, setHasAvitoIntegration] = useState(false);
  const [avitoVacancyId, setAvitoVacancyId] = useState<string | null>(null);
  const [avitoVacancyInput, setAvitoVacancyInput] = useState('');
  const [avitoLinkedVacancy, setAvitoLinkedVacancy] = useState<AvitoLinkedVacancy | null>(null);
  const [avitoLinkLoading, setAvitoLinkLoading] = useState(false);
  const [avitoImportLoading, setAvitoImportLoading] = useState(false);

  const [industryDisplayNames, setIndustryDisplayNames] = useState<string[]>([]);
  const [specializationDisplayNames, setSpecializationDisplayNames] = useState<string[]>([]);
  const [avitoSpecializationDisplayNames, setAvitoSpecializationDisplayNames] = useState<string[]>([]);
  const [countries, setCountries] = useState<CountryOption[]>([]);

  const specializationPathCacheKey = vacancyId ? `vacancySpecializationPathKeys_${vacancyId}` : null;

  useEffect(() => {
    if (vacancyId) {
      loadVacancyData();
    } else {
      setIsLoading(false);
      setSocialPackageText(brandingData?.benefits ?? '');
      apiService.getMyAccountSettings().then((settings: any) => {
        if (settings?.aboutCompany) {
          setCompanyDescription(prev => prev || settings.aboutCompany);
        }
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vacancyId]);

  // Доступные ATS-источники для блока "Вакансия из ATS".
  // /sources проверяет не только configured, но и фактическую доступность коннектора.
  useEffect(() => {
    let cancelled = false;
    getSources()
      .then((response: any) => {
        if (cancelled) return;
        setAtsSourceOptions(buildAvailableAtsSourceLinkOptions(response));
      })
      .catch(() => {
        if (!cancelled) setAtsSourceOptions([]);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const nextSource = getValidAtsSourceLinkSource(sourceLinkSource, atsSourceOptions);
    if (nextSource === sourceLinkSource) return;

    setSourceLinkSource(nextSource);
    setSourceLinkVacancyId('');
    setSourceLinkVacancyTitle('');
  }, [sourceLinkSource, atsSourceOptions]);

  useEffect(() => {
    let cancelled = false;
    getAvitoStatus()
      .then((status) => {
        if (cancelled) return;
        const connected = Boolean(status?.connected);
        setHasAvitoIntegration(connected);
      })
      .catch(() => {
        if (!cancelled) setHasAvitoIntegration(false);
      });
    return () => { cancelled = true; };
  }, []);

  // AI generation from initial prompt (from VacancyCreationModeSelector)
  useEffect(() => {
    if (!initialAiPrompt || vacancyId) return;
    const generateFromPrompt = async () => {
      setIsAiGenerating(true);
      try {
        const positionData = await apiService.generatePositionData(initialAiPrompt);
        if (positionData.generatedData) {
          if (positionData.generatedData.title) setJobTitle(positionData.generatedData.title);
          if (positionData.generatedData.suggestedDescription) setJobDescription(positionData.generatedData.suggestedDescription);
          if (positionData.generatedData.level) {
            const mappedLevel = mapApiLevelToUi(positionData.generatedData.level) || UI_LEVEL_FALLBACK;
            setLevel(mappedLevel);
            setMinScore(mapLevelToMinScore(mappedLevel));
          }
          toast.success('Данные вакансии сгенерированы');
        }
      } catch (error: any) {
        console.error('Ошибка генерации данных вакансии:', error);
        const backendMessage = error?.response?.data?.message;
        toast.error(backendMessage || 'Ошибка при генерации данных вакансии');
      } finally {
        setIsAiGenerating(false);
      }
    };
    generateFromPrompt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAiPrompt]);

  // Apply externally generated data (from AI assistant panel)
  useEffect(() => {
    if (!externalGeneratedData) return;
    const d = externalGeneratedData;

    // Basic fields
    if (d.title) setJobTitle(d.title);
    if (d.jobDescription) setJobDescription(d.jobDescription);
    if (d.suggestedDescription && !d.jobDescription) setJobDescription(d.suggestedDescription);
    if (d.companyDescription) setCompanyDescription(d.companyDescription);
    if (d.level) {
      const mappedLevel = mapApiLevelToUi(d.level) || UI_LEVEL_FALLBACK;
      setLevel(mappedLevel);
      setMinScore(mapLevelToMinScore(mappedLevel));
    }

    // Skills
    if (d.skills && Array.isArray(d.skills) && d.skills.length > 0) {
      // Handle both formats: [{type, skills}] (correct) and ["skill1", "skill2"] (flat array)
      const isFlat = typeof d.skills[0] === 'string';
      const newSkillInputs = isFlat
        ? [{
            id: '1',
            type: 'all' as const,
            skills: d.skills as string[],
            inputValue: '',
            skillExperience: {} as Record<string, string>,
            groupExperience: '',
          }]
        : d.skills
            .filter((group: any) => Array.isArray(group.skills) && group.skills.length > 0)
            .map((group: any, idx: number) => ({
              id: String(idx + 1),
              type: group.type === 'any' ? 'any' as const : 'all' as const,
              skills: group.skills,
              inputValue: '',
              skillExperience: group.skillExperience || {},
              groupExperience: '',
            }));
      if (newSkillInputs.length > 0) {
        setSkillsInputs(newSkillInputs);
      }
    }

    // Experience — AI returns enum values, form uses display labels
    const expEnumToLabel: Record<string, string> = {
      noExperience: 'Нет опыта',
      between1And3: 'От 1 года до 3 лет',
      between3And6: 'От 3 до 6 лет',
      moreThan6: 'Более 6 лет',
    };
    const mapExpValues = (val: any): string[] => {
      const arr = Array.isArray(val)
        ? val
        : String(val).split(',').map((s: string) => s.trim()).filter(Boolean);
      return arr.map((v: string) => expEnumToLabel[v] || v);
    };
    if (d.experienceInPosition) {
      setExperienceInPosition(mapExpValues(d.experienceInPosition));
    }
    if (d.totalExperience) {
      setTotalExperience(mapExpValues(d.totalExperience));
    }

    // Salary — only fill from/to amounts and NDA; don't touch salaryGross, salaryFrequency, salarySpecified
    if (d.salaryFrom) setSalaryFrom(String(d.salaryFrom));
    if (d.salaryTo) setSalaryTo(String(d.salaryTo));
    // Don't auto-set salaryGross (до вычета НДФЛ) from AI
    // Don't auto-set salaryFrequency from AI
    // Don't auto-check "указана зп" when filling salary from AI
    if (d.hasNda !== undefined) setHasNda(d.hasNda);

    // Education
    if (d.education && Array.isArray(d.education)) setEducation(d.education);

    // Geography — AI generates city names, resolve to IDs for saving
    if (d.cities && Array.isArray(d.cities) && d.cities.length > 0) {
      setSelectedAvitoLocationIds([]);
      setSelectedCities(d.cities);
      (async () => {
        const resolvedIds = await Promise.all(
          d.cities.map(async (city: string) => {
            if (/^\d+$/.test(city)) return city;
            const id = await getAreaIdByName(city);
            return id || city;
          })
        );
        const compressed = await compressCityIds(resolvedIds.filter(Boolean));
        setSelectedCityIds(compressed);
      })();
    }

    // Work format
    if (d.workFormat) setWorkFormat(d.workFormat);
    if (d.workSchedule) setWorkSchedule(d.workSchedule);

    // Office city, address — for office/hybrid formats
    // Also fill география with the same city
    if (d.officeCity) {
      (async () => {
        const cityId = await getAreaIdByName(d.officeCity);
        if (cityId) {
          setOfficeCityId(cityId);
          setOfficeCityName(d.officeCity);
          // Also set география with the same city for office/hybrid
          if (d.workFormat === 'Офис' || d.workFormat === 'Гибрид') {
            setSelectedAvitoLocationIds([]);
            setSelectedCities(prev => prev.includes(d.officeCity) ? prev : [d.officeCity]);
            const compressed = await compressCityIds([cityId]);
            setSelectedCityIds(compressed);
          }
        }
      })();
    }
    if (d.officeAddress) {
      setMetroEntries(prev => {
        const updated = [...prev];
        if (updated.length > 0) {
          updated[0] = { ...updated[0], address: d.officeAddress };
        } else {
          updated.push({ stationId: '', address: d.officeAddress });
        }
        return updated;
      });
    }

    // Employment
    if (d.employmentContractTypes && Array.isArray(d.employmentContractTypes)) {
      setEmploymentContractTypes(d.employmentContractTypes);
    }

    // Age
    if (d.ageFrom) setAgeFrom(String(d.ageFrom));
    if (d.ageTo) setAgeTo(String(d.ageTo));
    if (d.includeNoAge !== undefined) setIncludeNoAge(d.includeNoAge);

    // Gender
    if (d.gender) setGender(d.gender);

    // Checkboxes
    if (d.excludeJumpers !== undefined) setExcludeJumpers(d.excludeJumpers);
    if (d.useMetroInSearch !== undefined) setUseMetroInSearch(!!d.useMetroInSearch);
    if (d.hasBonuses !== undefined) setHasBonuses(d.hasBonuses);
    if (d.salarySpecified !== undefined) setSalarySpecified(d.salarySpecified);
    if (d.salaryGross !== undefined && d.salaryGross !== null) setSalaryGross(d.salaryGross);
    if (d.salaryFrequency) setSalaryFrequency(d.salaryFrequency);
    if (d.salaryCurrency) setSalaryCurrency(d.salaryCurrency);

    // Candidate search status
    if (d.candidateStatuses && Array.isArray(d.candidateStatuses)) setCandidateStatuses(d.candidateStatuses);

    // Relocation
    if (d.relocationLocation) setRelocationLocation(d.relocationLocation);
    if (d.relocationAssistance) setRelocationAssistance(d.relocationAssistance);

    // Working hours
    if (d.workingHours) setWorkingHours(d.workingHours);

    // Social package
    if (d.socialPackageText) setSocialPackageText(d.socialPackageText);

    // Industry & specialization — AI generates text names, resolve to HH IDs
    const resolveIndustryAndSpec = async () => {
      if (d.industry) {
        const rawVal = Array.isArray(d.industry) ? d.industry[0] : d.industry;
        // Check if it's already a numeric ID
        if (/^\d+$/.test(String(rawVal).trim())) {
          handleIndustrySelectionChange([String(rawVal)]);
        } else {
          // AI generated a text name — resolve to HH industry ID
          const id = await findIndustryIdByName(String(rawVal));
          if (id) {
            handleIndustrySelectionChange([id]);
          }
        }
      }
      if (d.specialization) {
        const rawVal = Array.isArray(d.specialization) ? d.specialization[0] : d.specialization;
        if (/^\d+/.test(String(rawVal).trim())) {
          // Already an ID or path key
          handleSpecializationSelectionChange([String(rawVal)]);
        } else {
          // AI generated a text name — resolve to HH specialization path key
          const pathKey = await findSpecializationPathKeyByName(String(rawVal));
          if (pathKey) {
            handleSpecializationSelectionChange([pathKey]);
          }
        }
      }
    };
    resolveIndustryAndSpec();

    void (async () => {
      if (d.citizenship != null) {
        const rawList = Array.isArray(d.citizenship) ? d.citizenship : [d.citizenship];
        const resolved: string[] = [];
        for (const item of rawList) {
          const s = String(item).trim();
          if (!s) continue;
          const id = await findCountryIdByName(s);
          if (id) resolved.push(id);
        }
        if (resolved.length > 0) {
          setCitizenship(resolved);
        }
      }

      const metroNamesRaw = d.metroStationNames;
      const hasMetroNames = Array.isArray(metroNamesRaw) && metroNamesRaw.length > 0;
      if (!hasMetroNames) {
        return;
      }

      let cityIdForMetro: string | null = null;
      if (d.officeCity) {
        cityIdForMetro = await getAreaIdByName(String(d.officeCity));
      }
      if (!cityIdForMetro && Array.isArray(d.cities) && d.cities.length > 0) {
        const c0 = d.cities[0];
        cityIdForMetro = /^\d+$/.test(String(c0)) ? String(c0) : await getAreaIdByName(String(c0));
      }

      const metroData = await fetchHHMetroCities();
      const rawName = String(metroNamesRaw![0]);
      const needle = rawName.toLowerCase().replace(/^м\.?\s*/i, '').trim();

      /** Не использовать needle.includes(sn): «Сокольники» ошибочно садилось на «Сокол». */
      const pickBestStationInCity = (city: MetroCityOption | undefined): { id: string; name: string } | null => {
        if (!city?.lines || !needle) return null;
        const list: { id: string; name: string; lc: string }[] = [];
        for (const line of city.lines) {
          for (const st of line.stations || []) {
            if (st.id && st.name) {
              list.push({ id: st.id, name: st.name, lc: st.name.toLowerCase() });
            }
          }
        }
        const exact = list.find((s) => s.lc === needle);
        if (exact) return { id: exact.id, name: exact.name };
        const byPrefix = list.filter((s) => s.lc.startsWith(needle));
        if (byPrefix.length > 0) {
          byPrefix.sort((a, b) => a.lc.length - b.lc.length);
          return { id: byPrefix[0].id, name: byPrefix[0].name };
        }
        const bySubstring = list.filter((s) => s.lc.includes(needle));
        if (bySubstring.length > 0) {
          bySubstring.sort((a, b) => a.lc.length - b.lc.length);
          return { id: bySubstring[0].id, name: bySubstring[0].name };
        }
        return null;
      };

      const findStationInCity = (
        city: MetroCityOption | undefined
      ): { stationId: string; city: MetroCityOption } | null => {
        const st = pickBestStationInCity(city);
        return st && city ? { stationId: st.id, city } : null;
      };

      let match: { stationId: string; city: MetroCityOption } | null = null;
      if (cityIdForMetro) {
        match = findStationInCity(metroData.find((c) => String(c.id) === String(cityIdForMetro)));
      }
      if (!match) {
        for (const city of metroData) {
          const m = findStationInCity(city);
          if (m) {
            match = m;
            break;
          }
        }
      }

      if (match) {
        const stationId = match.stationId;
        setOfficeCityId(match.city.id);
        setOfficeCityName(match.city.name);
        setMetroEntries((prev) => {
          const next = prev.length > 0 ? [...prev] : [{ stationId: '', address: '' }];
          next[0] = { ...next[0], stationId };
          return next;
        });
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalGeneratedData]);

  // Load HH vacancies on mount
  useEffect(() => {
    const loadHHVacancies = async () => {
      setHhVacanciesLoading(true);
      try {
        const response = await getHHActiveVacancies();
        // API returns { success: true, vacancies: { items: [...] } }
        const items = response?.vacancies?.items || response?.items || [];
        setHhVacancies(Array.isArray(items) ? items : []);
      } catch (err) {
        console.error('Error loading HH vacancies:', err);
        setHhVacancies([]);
      } finally {
        setHhVacanciesLoading(false);
      }
    };
    void loadHHVacancies();
  }, []);

  useEffect(() => {
    const loadHHEmployerAddresses = async () => {
      setHhEmployerAddressesLoading(true);
      try {
        const response = await getHHEmployerAddresses();
        const items = response?.items || [];
        setHhEmployerAddresses(Array.isArray(items) ? items.filter(item => !item.deleted) : []);
      } catch (err) {
        console.error('Error loading HH employer addresses:', err);
        setHhEmployerAddresses([]);
      } finally {
        setHhEmployerAddressesLoading(false);
      }
    };
    void loadHHEmployerAddresses();
  }, []);

  // Trigger HH import from AI panel
  useEffect(() => {
    if (hhImportVacancyId) {
      applyHHVacancyData(hhImportVacancyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hhImportVacancyId]);

  // Выбор HH-вакансии из своего списка в AI-помощнике (только связываем, без импорта
  // полей). Вакансия уже есть в hhVacancies (пришла из getHHActiveVacancies), поэтому
  // имя в дропдауне отобразится автоматически. AI-чат заполняет остальные поля формы
  // из описания.
  useEffect(() => {
    if (initialHhVacancyLink) {
      setHhVacancyId(initialHhVacancyLink);
      setHhAdditionalVacancyIds(prev => prev.filter(id => id !== initialHhVacancyLink));
    }
  }, [initialHhVacancyLink]);

  useEffect(() => {
    fetchHHCountries()
      .then(setCountries)
      .catch(() => setCountries([]));
  }, []);

  useEffect(() => {
    const needsMetro = workFormat === 'Офис' || workFormat === 'Гибрид';
    if (!needsMetro) {
      if (officeCityId) setOfficeCityId('');
      setMetroEntries([{stationId: '', address: ''}]);
      setUseMetroInSearch(false);
      return;
    }
    if (metroCities.length === 0) {
      fetchHHMetroCities()
        .then(setMetroCities)
        .catch((e) => {
          console.error('Ошибка загрузки справочника метро:', e);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workFormat, metroCities.length]);

  // Загрузка справочника графиков работы из HH
  useEffect(() => {
    fetchHHWorkSchedules()
      .then(setHhWorkSchedules)
      .catch((e) => {
        console.error('Ошибка загрузки справочника графиков работы:', e);
      });
  }, []);

  // Загрузка справочника валют из HH
  useEffect(() => {
    fetchHHCurrencies()
      .then(setHhCurrencies)
      .catch((e) => {
        console.error('Ошибка загрузки справочника валют:', e);
      });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formContainerRef.current) {
        formContainerRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [activeTab]);

  const handleAvitoAreaSelectionChange = async (areas: string[]) => {
    const avitoLocationIds = await compressAvitoLocationIds(
      areas.map(String).map((item) => item.trim()).filter(Boolean)
    );
    setSelectedAvitoLocationIds(avitoLocationIds);

    if (avitoLocationIds.length === 0) {
      setSelectedCities([]);
      setSelectedCityIds([]);
      setStrictRegionSearch(false);
      setHhAreaFallbackSubtitle(DEFAULT_HH_AREA_FALLBACK_SUBTITLE);
      return;
    }

    const names = await Promise.all(
      avitoLocationIds.map(async (id) => {
        try {
          return await getAvitoLocationNameById(id);
        } catch {
          return id;
        }
      })
    );
    setSelectedCities(names);

    try {
      const mapping = await resolveAvitoLocationsToHH(avitoLocationIds);
      const compressedIds = await compressCityIds(mapping.mappedAreaIds || []);
      setSelectedCityIds(compressedIds);

      if ((mapping.unmapped || []).length > 0 || compressedIds.length === 0) {
        setHhAreaFallbackSubtitle(buildHhAreaFallbackSubtitle(mapping.unmapped || []));
        setIsHhAreaFallbackModalOpen(true);
      }
    } catch (error) {
      console.error('Ошибка маппинга географии Avito на HH:', error);
      setSelectedCityIds([]);
      setHhAreaFallbackSubtitle(HH_AREA_MAPPING_ERROR_SUBTITLE);
      setIsHhAreaFallbackModalOpen(true);
    }
  };

  const handleHhAreaFallbackSelectionChange = async (areas: string[]) => {
    const compressedIds = await compressCityIds(areas);
    setSelectedCityIds(compressedIds);
    if (selectedAvitoLocationIds.length === 0) {
      const names = await Promise.all(
        compressedIds.map(async (id) => {
          try {
            return await getAreaNameById(id);
          } catch {
            return id;
          }
        })
      );
      setSelectedCities(names);
    }
    setIsHhAreaFallbackModalOpen(false);
    if (compressedIds.length === 0) setStrictRegionSearch(false);
  };

  const handleIndustrySelectionChange = async (industries: string[], filterByExpIndustryValue?: boolean) => {
    setIndustry(industries);
    // Сбрасываем фильтр если отрасли не выбраны
    if (industries.length === 0) {
      setFilterByExpIndustry(false);
    } else if (filterByExpIndustryValue !== undefined) {
      setFilterByExpIndustry(filterByExpIndustryValue);
    }
    if (industries.length > 0) {
      try {
        const names = await Promise.all(
          industries.map(async (id) => {
            try {
              const normalizedId = String(id).trim();
              const name = await getIndustryNameById(normalizedId);
              return name && name !== normalizedId ? name : normalizedId;
            } catch (error) {
              console.error(`Error loading industry name for ID ${id}:`, error);
              return String(id);
            }
          })
        );
        const filteredNames = names.filter(Boolean);
        setIndustryDisplayNames(filteredNames);
      } catch (error) {
        console.error('Error loading industry names:', error);
        setIndustryDisplayNames(industries);
      }
    } else {
      setIndustryDisplayNames([]);
    }
  };

  const handleSpecializationSelectionChange = async (specializations: string[]) => {
    setSpecialization(specializations);
    if (specializationPathCacheKey) {
      try {
        localStorage.setItem(specializationPathCacheKey, JSON.stringify(specializations));
      } catch {}
    }
    if (specializations.length > 0) {
      try {
        const names = await Promise.all(
          specializations.map(async (id) => {
            try {
              const normalizedId = String(id).trim();
              const name = await getStoredSpecializationName(normalizedId);
              return name && name !== normalizedId ? name : normalizedId;
            } catch (error) {
              console.error(`Error loading specialization name for ID ${id}:`, error);
              return String(id);
            }
          })
        );
        const filteredNames = names.filter(Boolean);
        setSpecializationDisplayNames(filteredNames);
      } catch (error) {
        console.error('Error loading specialization names:', error);
        setSpecializationDisplayNames(specializations);
      }
    } else {
      setSpecializationDisplayNames([]);
    }
  };

  const updateAvitoSpecializationDisplayNames = async (specializations: string[]) => {
    if (specializations.length === 0) {
      setAvitoSpecializationDisplayNames([]);
      return;
    }

    try {
      const names = await Promise.all(
        specializations.map(async (id) => {
          const normalizedId = String(id).trim();
          const name = await getAvitoResumeSpecializationNameById(normalizedId);
          return name && name !== normalizedId ? name : normalizedId;
        })
      );
      setAvitoSpecializationDisplayNames(names.filter(Boolean));
    } catch (error) {
      console.error('Error loading Avito specialization names:', error);
      setAvitoSpecializationDisplayNames(specializations);
    }
  };

  const handleAvitoSpecializationSelectionChange = async (specializations: string[]) => {
    const normalized = specializations.map(String).map((item) => item.trim()).filter(Boolean);
    setAvitoSpecialization(normalized);
    await updateAvitoSpecializationDisplayNames(normalized);
  };

  const restoreAvitoVacancyLink = async (rawVacancyId: unknown) => {
    const linkedVacancyId = String(rawVacancyId || '').trim();
    if (!linkedVacancyId) {
      setAvitoVacancyId(null);
      setAvitoVacancyInput('');
      setAvitoLinkedVacancy(null);
      setAvitoLinkLoading(false);
      return;
    }

    setAvitoVacancyId(linkedVacancyId);
    setAvitoVacancyInput(linkedVacancyId);
    setAvitoLinkedVacancy(createAvitoLinkedVacancyPlaceholder(linkedVacancyId));
    setAvitoLinkLoading(true);

    try {
      const response = await getAvitoVacancyDetail(linkedVacancyId);
      const data = response?.vacancyDetail;
      if (!data) return;

      const detailedVacancyId = String((data as any).avitoVacancyId || linkedVacancyId);
      setAvitoVacancyId(detailedVacancyId);
      setAvitoVacancyInput(detailedVacancyId);
      setAvitoLinkedVacancy(data);
    } catch (error) {
      console.error('Error restoring Avito vacancy link:', error);
    } finally {
      setAvitoLinkLoading(false);
    }
  };

  const loadVacancyData = async () => {
    if (!vacancyId) return;

    try {
      setIsLoading(true);
      setIndustryDisplayNames([]);
      setSpecializationDisplayNames([]);
      setAvitoSpecializationDisplayNames([]);
      
      const data = await apiService.getPosition(vacancyId);

      setJobTitle(data.title || '');
      setJobDescription(data.description || '');
      
      let loadedSkillsInputs: SkillsInputField[] = [];
      
      if ((data as any).skillGroups) {
        try {
          const parsedGroups = typeof (data as any).skillGroups === 'string' 
            ? JSON.parse((data as any).skillGroups) 
            : (data as any).skillGroups;
          
          if (Array.isArray(parsedGroups) && parsedGroups.length > 0) {
            console.log('Parsed skillGroups:', parsedGroups);
            loadedSkillsInputs = parsedGroups.map((group: any, index: number) => {
              let groupType: 'any' | 'all' = 'any';
              if (group.type === 'all' || group.type === 'AND') {
                groupType = 'all';
              } else if (group.type === 'any' || group.type === 'OR') {
                groupType = 'any';
              }
              const groupSkills = Array.isArray(group.skills) 
                ? group.skills.filter((s: string) => s && s.trim()) 
                : [];
              console.log(`Creating group ${index + 1}:`, { 
                originalType: group.type, 
                resolvedType: groupType, 
                skills: groupSkills 
              });
              return {
                id: String(index + 1),
                type: groupType,
                skills: groupSkills,
                inputValue: '',
                skillExperience: groupType === 'all' ? (group.skillExperience || {}) : {},
                groupExperience: groupType === 'any' ? (group.groupExperience || '') : ''
              };
            });
            console.log('Total loaded groups:', loadedSkillsInputs.length);
          }
        } catch (error) {
          console.error('Error parsing skillGroups:', error);
        }
      }
      
      if (loadedSkillsInputs.length === 0) {
        if (data.topics && data.topics.length > 0) {
          loadedSkillsInputs = [{ id: '1', type: 'any', skills: data.topics, inputValue: '', skillExperience: {}, groupExperience: '' }];
        } else {
          loadedSkillsInputs = [{ id: '1', type: 'any', skills: [], inputValue: '', skillExperience: {}, groupExperience: '' }];
        }
      }
      
      console.log('Final loadedSkillsInputs before setState:', loadedSkillsInputs);
      console.log('Number of groups to display:', loadedSkillsInputs.length);
      setSkillsInputs(loadedSkillsInputs);
      setMinScore(data.minScore || 5);
      const apiLevel = data.level || PositionLevelEnum.MIDDLE;
      setLevel(levelMap[apiLevel as PositionLevelEnum] || UI_LEVEL_FALLBACK);
      setStatus(data.status || PositionStatusEnum.ACTIVE);
      const answerTime = data.answerTime || 120;
      const minutes = Math.floor(answerTime / 60);
      setTimePerAnswer(`${minutes} мин`);
      setQuestionCount(data.questionsCount || 5);

      let industryArray: string[] = [];
      if (data.industry) {
        if (Array.isArray(data.industry)) {
          industryArray = data.industry.filter(Boolean).map(String);
        } else if (typeof data.industry === 'string' && data.industry.trim()) {
          industryArray = data.industry.split(',').map((s: string) => s.trim()).filter(Boolean).map(String);
        }
      }
      
      let specializationArray: string[] = [];
      if (data.specialization) {
        if (Array.isArray(data.specialization)) {
          specializationArray = data.specialization.filter(Boolean).map(String);
        } else if (typeof data.specialization === 'string' && data.specialization.trim()) {
          specializationArray = data.specialization.split(',').map((s: string) => s.trim()).filter(Boolean).map(String);
        }
      }

      let avitoSpecializationArray: string[] = [];
      const rawAvitoSpecialization = (data as any).avitoSpecialization;
      if (rawAvitoSpecialization) {
        if (Array.isArray(rawAvitoSpecialization)) {
          avitoSpecializationArray = rawAvitoSpecialization.filter(Boolean).map(String);
        } else if (typeof rawAvitoSpecialization === 'string' && rawAvitoSpecialization.trim()) {
          avitoSpecializationArray = rawAvitoSpecialization.split(',').map((s: string) => s.trim()).filter(Boolean).map(String);
        }
      }

      setIndustry(industryArray);
      setAvitoSpecialization(avitoSpecializationArray);
      updateAvitoSpecializationDisplayNames(avitoSpecializationArray);
      let selectedSpecializationArray = specializationArray;
      if (specializationPathCacheKey) {
        try {
          const raw = localStorage.getItem(specializationPathCacheKey);
          const cached = raw ? JSON.parse(raw) : null;
          const cachedArr = Array.isArray(cached) ? cached.map(String).filter(Boolean) : [];
          selectedSpecializationArray = isSameHhStoredSpecializationSelection(specializationArray, cachedArr)
            ? cachedArr
            : specializationArray;
        } catch {
          selectedSpecializationArray = specializationArray;
        }
      }
      setSpecialization(selectedSpecializationArray);
      
      if (industryArray.length > 0) {
        try {
          const industryNames = await Promise.all(
            industryArray.map(async (id) => {
              try {
                const normalizedId = String(id).trim();
                const name = await getIndustryNameById(normalizedId);
                return name && name !== normalizedId ? name : normalizedId;
              } catch (error) {
                console.error(`Error loading industry name for ID ${id}:`, error);
                return String(id);
              }
            })
          );
          const filteredIndustryNames = industryNames.filter(Boolean);
          setIndustryDisplayNames(filteredIndustryNames);
        } catch (error) {
          console.error('Error loading industry names:', error);
          setIndustryDisplayNames(industryArray);
        }
      } else {
        setIndustryDisplayNames([]);
      }
      
      if (selectedSpecializationArray.length > 0) {
        try {
          const specializationNames = await Promise.all(
            selectedSpecializationArray.map(async (id) => {
              try {
                const normalizedId = String(id).trim();
                const name = await getStoredSpecializationName(normalizedId);

                return name && name !== normalizedId ? name : normalizedId;
              } catch (error) {
                console.error(`Error loading specialization name for ID ${id}:`, error);
                return String(id);
              }
            })
          );
          const filteredSpecializationNames = specializationNames.filter(Boolean);
          setSpecializationDisplayNames(filteredSpecializationNames);
        } catch (error) {
          console.error('Error loading specialization names:', error);
          setSpecializationDisplayNames(selectedSpecializationArray);
        }
      } else {
        setSpecializationDisplayNames([]);
      }

      // География: поддержка старых данных (названия) и новых (ID)
      const rawCities = (data.cities || []).map((c: any) => String(c));
      let cityIds: string[] = [];

      if (rawCities.length > 0) {
        const allIds = rawCities.every((city) => /^\d+$/.test(city));

        if (allIds) {
          cityIds = rawCities;
        } else {
          try {
            const ids: string[] = [];
            for (const cityName of rawCities) {
              const id = await getAreaIdByName(String(cityName));
              if (id) ids.push(id);
            }
            if (ids.length > 0) {
              cityIds = ids;
            }
          } catch (e) {
            console.error('Ошибка восстановления cityIds из cities:', e);
          }
        }
      }

      const compressedIds = await compressCityIds(cityIds);
      const compressedNames = await Promise.all(
        compressedIds.map(async (id) => {
          try {
            return await getAreaNameById(id);
          } catch {
            return id;
          }
        })
      );
      const rawAvitoLocationIds = Array.isArray((data as any).avitoLocationIds)
        ? (data as any).avitoLocationIds.map((id: any) => String(id).trim()).filter(Boolean)
        : [];
      setSelectedAvitoLocationIds(rawAvitoLocationIds);
      setSelectedCityIds(compressedIds);
      if (rawAvitoLocationIds.length > 0) {
        try {
          const avitoNames = await Promise.all(
            rawAvitoLocationIds.map(async (id: string) => {
              try {
                return await getAvitoLocationNameById(id);
              } catch {
                return id;
              }
            })
          );
          setSelectedCities(avitoNames);
        } catch {
          setSelectedCities(rawAvitoLocationIds);
        }
      } else {
        setSelectedCities(compressedNames);
      }
      setStrictRegionSearch((data as any).strictRegionSearch ?? false);
      setCandidateStatuses(data.candidateStatuses || []);
      const expInPosition = data.experienceInPosition || '';
      setExperienceInPosition(expInPosition ? expInPosition.split(',').map(s => s.trim()).filter(Boolean) : []);
      setExperiencePeriod(data.experiencePeriod || '');
      const totalExp = data.totalExperience || '';
      setTotalExperience(totalExp ? totalExp.split(',').map(s => s.trim()).filter(Boolean) : []);
      setExcludeJumpers((data as any).excludeJumpers || false);
      setFilterByExpIndustry((data as any).filterByExpIndustry || false);
      setSalaryFrom(data.salaryFrom?.toString() || '');
      setSalaryTo(data.salaryTo?.toString() || '');
      if (Array.isArray(data.education)) {
        setEducation(data.education.map(String).map((s) => s.trim()).filter(Boolean));
      } else if (data.education) {
        setEducation(String(data.education).split(',').map((s) => s.trim()).filter(Boolean));
      } else {
        setEducation([]);
      }
      setAgeFrom(data.ageFrom?.toString() || '');
      setAgeTo(data.ageTo?.toString() || '');
      setIncludeNoAge(data.includeNoAge || false);
      setGender(data.gender || 'any');
      const loadedCitizenship = Array.isArray((data as any).citizenship)
        ? (data as any).citizenship.map(String).filter(Boolean)
        : typeof (data as any).citizenship === 'string' && (data as any).citizenship.trim()
        ? (data as any).citizenship.split(',').map((value: string) => value.trim()).filter(Boolean)
        : [];
      setCitizenship(loadedCitizenship);
      setCompanyDescription(data.companyDescription || '');
      setWorkFormat(data.workFormat || '');
      setWorkSchedule(data.workSchedule || '');
      const anyData = data as any;
      const loadedOfficeCityId = anyData.officeCityId ? String(anyData.officeCityId) : '';
      setOfficeCityId(loadedOfficeCityId);
      if (loadedOfficeCityId) {
        getAreaNameById(loadedOfficeCityId).then(name => setOfficeCityName(name || ''));
      }
      // Восстановление officeLocations (пары станция + адрес)
      const locations: Array<{metroStationId?: string; address?: string; hhAddressId?: string}> = Array.isArray(anyData.officeLocations) ? anyData.officeLocations : [];
      if (locations.length > 0) {
        setMetroEntries(locations.map(loc => ({
          stationId: loc.metroStationId ? String(loc.metroStationId) : '',
          address: loc.address ? String(loc.address) : '',
          hhAddressId: loc.hhAddressId ? String(loc.hhAddressId) : undefined,
        })));
      } else {
        setMetroEntries([{ stationId: '', address: '' }]);
      }
      setUseMetroInSearch(anyData.useMetroInSearch === true);
      setEmploymentContractTypes(data.employmentContractTypes || []);
      setSocialPackageText((data as any).socialPackageText ?? '');
      setRelocationLocation(data.relocationLocation || '');
      setRelocationAssistance(data.relocationAssistance || '');
      setHasNda(data.hasNda || false);
      setHasBonuses(data.hasBonuses || false);
      setSalarySpecified(data.salarySpecified || false);
      setSalaryGross((data as any).salaryGross ?? null);
      setSalaryFrequency((data as any).salaryFrequency || '');
      setSalaryCurrency(data.salaryCurrency || 'RUR');
      setWorkingHours((data as any).workingHours || '');
      const loadedInternalTag = (data as any).internalTag || '';
      setInternalTag(loadedInternalTag);
      setShowInternalTagInput(!!loadedInternalTag);

      setIsAiScreeningEnabled(Boolean((data as any).aiScreeningEnabled));
      setIsHrInterviewEnabled((data as any).hrInterviewEnabled !== false);
      setIsScoringEnabled((data as any).scoringEnabled !== false);

      // Load requirements with weights
      if ((data as any).aiRequirementsWeighted) {
        try {
          const raw = (data as any).aiRequirementsWeighted;
          const w = typeof raw === 'string' ? JSON.parse(raw) : raw;
          loadedAiRequirementsFullRef.current = w && typeof w === 'object' ? w : null;
          requirementsEditedInVacancyFormRef.current = false;
          setInternalRequirements({
            required: (w.required || []).map((r: any, i: number) => ({ id: `req-${i}`, text: r.text, weight: r.weight ?? 7 })),
            optional: (w.optional || []).map((r: any, i: number) => ({ id: `opt-${i}`, text: r.text, weight: r.weight ?? 3 })),
          });
        } catch (e) {
          console.error('Error parsing aiRequirementsWeighted:', e);
          loadedAiRequirementsFullRef.current = null;
          requirementsEditedInVacancyFormRef.current = false;
          setInternalRequirements({ required: [], optional: [] });
        }
      } else {
        // No requirements yet — initialize empty so user can add them
        loadedAiRequirementsFullRef.current = null;
        requirementsEditedInVacancyFormRef.current = false;
        setInternalRequirements({ required: [], optional: [] });
      }
      setShowInternalReqPanel(true);

      // HH.ru vacancy linking
      setHhVacancyId((data as any).hhVacancyId || null);
      setHhAdditionalVacancyIds((data as any).hhAdditionalVacancyIds || []);
      let loadedAvitoVacancyId = String((data as any).avitoVacancyId || '').trim();

      // FR-E2E-6: Загрузка привязок из любых источников
      if (vacancyId) {
        try {
          const links = await apiService.getSourceLinks(vacancyId);
          const avitoLink = links.find((link) => String(link.source || '').toUpperCase() === 'AVITO');
          setSourceLinks(links.filter((link) => String(link.source || '').toUpperCase() !== 'AVITO'));
          if (!loadedAvitoVacancyId && avitoLink?.externalVacancyId) {
            loadedAvitoVacancyId = String(avitoLink.externalVacancyId).trim();
          }
        } catch (e) {
          // Не блокируем загрузку формы
        }
      }
      void restoreAvitoVacancyLink(loadedAvitoVacancyId);

      toast.success('Данные вакансии загружены');
    } catch (error: any) {
      console.error('Ошибка загрузки вакансии:', error);
      const backendMessage = error?.response?.data?.message;
      toast.error(backendMessage || 'Не удалось загрузить данные вакансии');
    } finally {
      setIsLoading(false);
    }
  };

  // Apply HH vacancy data to form fields (import)
  const applyHHVacancyData = async (hhVacancyIdToImport: string) => {
    setHhImportLoading(true);
    try {
      const response = await getHHVacancyDetail(hhVacancyIdToImport);
      const data = response?.vacancyDetail;
      if (!data) {
        toast.error('Не удалось загрузить данные вакансии с HH.ru');
        return;
      }
      setSelectedAvitoLocationIds([]);

      // Fill form fields from HH data
      if (data.title) setJobTitle(data.title);
      if (data.description) setJobDescription(data.description);
      if (data.workFormat) setWorkFormat(data.workFormat);
      if (data.workSchedule) setWorkSchedule(data.workSchedule);
      // Город в разделе "Формат работы" — заполнять только для Офис/Гибрид
      // (при удалёнке город не нужен; если пользователь сменит формат — выберет город сам)
      const needsOfficeCity = data.workFormat === 'Офис' || data.workFormat === 'Гибрид';
      if (data.officeCityId && needsOfficeCity) {
        setOfficeCityId(String(data.officeCityId));
        getAreaNameById(String(data.officeCityId)).then(name => setOfficeCityName(name || ''));
      }
      if (data.totalExperience) setTotalExperience([data.totalExperience]);

      // Тип оформления из HH: accept_labor_contract → tk_rf, civil_law_contracts → ip
      if (data.employmentContractTypes && data.employmentContractTypes.length > 0) {
        setEmploymentContractTypes(data.employmentContractTypes);
      }

      // Specialization (HH professional role ID → full path key)
      if (data.specializationId) {
        const pathKey = await findSpecializationPathKeyById(data.specializationId);
        handleSpecializationSelectionChange([pathKey]);
      }

      // Industry (HH industry ID)
      if (data.industryId) {
        setIndustry([data.industryId]);
      }

      // Salary
      if (data.salaryFrom != null) {
        setSalaryFrom(formatNumberWithSpaces(String(data.salaryFrom)));
        setSalarySpecified(true);
      }
      if (data.salaryTo != null) {
        setSalaryTo(formatNumberWithSpaces(String(data.salaryTo)));
        setSalarySpecified(true);
      }
      if (data.salaryGross != null) {
        setSalaryGross(data.salaryGross);
      }
      if (data.salaryFrequency) setSalaryFrequency(data.salaryFrequency);
      if (data.salaryCurrency) setSalaryCurrency(data.salaryCurrency);

      // Рабочие часы и адрес HH
      if (data.workingHours) setWorkingHours(data.workingHours);

      // Cities
      if (data.cities && data.cities.length > 0) {
        setSelectedAvitoLocationIds([]);
        setSelectedCities(data.cities);
        try {
          const cityIds = await Promise.all(
            data.cities.map(async (name: string) => {
              try { return await getAreaIdByName(name); } catch { return name; }
            })
          );
          setSelectedCityIds(cityIds.filter(Boolean));
        } catch { /* skip city ID resolution */ }
      }

      // Office locations from HH.ru (pairs of metro station + address) — только для Офис/Гибрид
      if (needsOfficeCity) {
        const hhLocations: Array<{metroStationId?: string; address?: string; hhAddressId?: string}> = Array.isArray(data.officeLocations) ? data.officeLocations : [];
        if (hhLocations.length > 0) {
          setMetroEntries(hhLocations.map(loc => ({
            stationId: loc.metroStationId ? String(loc.metroStationId) : '',
            address: loc.address ? String(loc.address) : '',
            hhAddressId: loc.hhAddressId ? String(loc.hhAddressId) : undefined,
          })));
        }
      }

      // Skills — import as a single "all" (AND) group: HH key_skills это перечень
      // НУЖНЫХ навыков, а не взаимозаменяемые альтернативы. Раньше был "any" — из-за
      // этого, например, PowerPoint и Power BI схлопывались в "один из них", хотя в
      // вакансии они — отдельные обязательные навыки. Если пользователь видит
      // взаимозаменяемые ("PostgreSQL или MySQL") — он сам пересоберёт группы в "any"
      // после импорта.
      if (data.skills && data.skills.length > 0) {
        setSkillsInputs([{
          id: '1',
          type: 'all',
          skills: data.skills,
          inputValue: '',
          skillExperience: {},
          groupExperience: ''
        }]);
      }

      // Set this vacancy as linked
      setHhVacancyId(hhVacancyIdToImport);

      toast.success('Данные импортированы из HH.ru');
    } catch (error: any) {
      console.error('Error importing HH vacancy:', error);
      const message = error?.response?.data?.error?.message || error?.message || 'Ошибка при импорте данных с HH.ru';
      if (message.includes('авторизо') || message.includes('auth')) {
        toast.error('Необходимо повторно авторизоваться в HH.ru');
      } else {
        toast.error(message);
      }
    } finally {
      setHhImportLoading(false);
    }
  };

  // Handle HH vacancy selection — just store the ID
  const handleHHVacancySelect = (newVacancyId: string | null) => {
    if (!newVacancyId) {
      setHhVacancyId(null);
      return;
    }
    // Remove from additional if selected as main
    setHhAdditionalVacancyIds(prev => prev.filter(id => id !== newVacancyId));
    setHhVacancyId(newVacancyId);
  };

  // Импорт данных из выбранной HH-вакансии в форму
  const handleImportFromHH = async () => {
    if (!hhVacancyId) return;
    await applyHHVacancyData(hhVacancyId);
  };

  const extractAvitoVacancyId = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (/^\d+$/.test(trimmed)) return trimmed;

    let source = trimmed;
    try {
      source = new URL(trimmed).pathname;
    } catch {
      // Значение может быть не полной ссылкой, а кусочком пути Авито.
    }

    let decoded = source;
    try {
      decoded = decodeURIComponent(source);
    } catch {
      decoded = source;
    }
    return (
      decoded.match(/(?:_|\/)(\d{6,})(?:[/?#]|$)/)?.[1]
      || decoded.match(/(\d{6,})(?:[/?#]|$)/)?.[1]
      || ''
    );
  };

  const showAvitoVacancyError = (error: any, fallbackMessage: string) => {
    console.error('Error loading Avito vacancy:', error);
    const responseData = error?.response?.data;
    const message = responseData?.message || error?.message || fallbackMessage;
    if (responseData?.code === 'no_credentials') {
      toast.error('Авито не подключено для вашей компании');
    } else {
      toast.error(message);
    }
  };

  const applyAvitoVacancyDetail = async (data: AvitoLinkedVacancy, fallbackVacancyId: string) => {
    if (data.title) setJobTitle(data.title);
    if (data.description) setJobDescription(data.description);
    if (data.workFormat) setWorkFormat(data.workFormat);
    if (data.workSchedule) setWorkSchedule(data.workSchedule);
    if (data.workingHours) setWorkingHours(data.workingHours);
    if (data.totalExperience) setTotalExperience([data.totalExperience]);

    if (Array.isArray(data.education)) {
      setEducation(data.education.map((item: unknown) => String(item).trim()).filter(Boolean));
    } else if (data.education) {
      setEducation(String(data.education).split(',').map((s: string) => s.trim()).filter(Boolean));
    }

    const nextEmploymentContractTypes = Array.isArray(data.employmentContractTypes)
      ? data.employmentContractTypes.map((item: unknown) => String(item).trim()).filter(Boolean)
      : String(data.employmentContractTypes || '').split(',').map((item: string) => item.trim()).filter(Boolean);
    if (nextEmploymentContractTypes.length > 0) {
      setEmploymentContractTypes(nextEmploymentContractTypes);
    }

    const avitoSpecializations = Array.isArray(data.avitoSpecialization)
      ? data.avitoSpecialization.map((item: unknown) => String(item).trim()).filter(Boolean)
      : String(data.avitoSpecialization || '').split(',').map((item: string) => item.trim()).filter(Boolean);
    if (avitoSpecializations.length > 0) {
      setAvitoSpecialization(avitoSpecializations);
      updateAvitoSpecializationDisplayNames(avitoSpecializations).catch(() => {});
    }

    const avitoLocationIds = Array.isArray((data as any).avitoLocationIds)
      ? (data as any).avitoLocationIds.map((item: unknown) => String(item).trim()).filter(Boolean)
      : String((data as any).avitoLocationIds || '').split(',').map((item: string) => item.trim()).filter(Boolean);
    if (avitoLocationIds.length > 0) {
      await handleAvitoAreaSelectionChange(avitoLocationIds);
    }

    if (data.salaryFrom != null) {
      setSalaryFrom(formatNumberWithSpaces(String(data.salaryFrom)));
      setSalarySpecified(true);
    }
    if (data.salaryTo != null) {
      setSalaryTo(formatNumberWithSpaces(String(data.salaryTo)));
      setSalarySpecified(true);
    }
    if (data.salaryGross != null) setSalaryGross(data.salaryGross);
    if (data.salaryFrequency) setSalaryFrequency(data.salaryFrequency);
    if (data.salaryCurrency) setSalaryCurrency(data.salaryCurrency);

    if (data.hasBonuses !== undefined) setHasBonuses(Boolean(data.hasBonuses));
    if (data.socialPackageText) setSocialPackageText(data.socialPackageText);

    if (data.internalTag) {
      setInternalTag(data.internalTag);
      setShowInternalTagInput(true);
    }

    const needsOfficeAddress = data.workFormat === 'Офис' || data.workFormat === 'Гибрид';
    if (needsOfficeAddress && data.officeAddress) {
      setMetroEntries([{ stationId: '', address: String(data.officeAddress) }]);
    }

    const linkedVacancyId = String((data as any).avitoVacancyId || fallbackVacancyId);
    setAvitoVacancyId(linkedVacancyId);
    setAvitoVacancyInput(linkedVacancyId);
    setAvitoLinkedVacancy(data);
  };

  const handleAttachAvitoVacancy = async () => {
    const avitoVacancyIdToAttach = extractAvitoVacancyId(avitoVacancyInput);
    if (!avitoVacancyIdToAttach) {
      toast.error('Вставьте ссылку или ID вакансии Авито');
      return;
    }

    setAvitoLinkLoading(true);
    try {
      const response = await getAvitoVacancyDetail(avitoVacancyIdToAttach);
      const data = response?.vacancyDetail;
      if (!data) {
        toast.error('Не удалось загрузить данные вакансии с Авито');
        return;
      }

      const linkedVacancyId = String((data as any).avitoVacancyId || avitoVacancyIdToAttach);
      setAvitoVacancyId(linkedVacancyId);
      setAvitoVacancyInput(linkedVacancyId);
      setAvitoLinkedVacancy(data);
      toast.success('Вакансия Авито привязана');
    } catch (error: any) {
      showAvitoVacancyError(error, 'Ошибка при привязке вакансии Авито');
    } finally {
      setAvitoLinkLoading(false);
    }
  };

  const handleImportFromAvito = async () => {
    if (!avitoVacancyId || !avitoLinkedVacancy) {
      toast.error('Сначала привяжите вакансию Авито');
      return;
    }

    setAvitoImportLoading(true);
    try {
      let vacancyDetail = avitoLinkedVacancy;
      if (vacancyDetail.__placeholder) {
        const response = await getAvitoVacancyDetail(avitoVacancyId);
        vacancyDetail = response?.vacancyDetail;
        if (!vacancyDetail) {
          toast.error('Не удалось загрузить данные вакансии с Авито');
          return;
        }
        setAvitoLinkedVacancy(vacancyDetail);
      }

      await applyAvitoVacancyDetail(vacancyDetail, avitoVacancyId);
      toast.success('Данные импортированы из Авито');
    } catch (error: any) {
      showAvitoVacancyError(error, 'Ошибка при импорте данных с Авито');
    } finally {
      setAvitoImportLoading(false);
    }
  };

  const handleAvitoVacancyInputChange = (value: string) => {
    setAvitoVacancyInput(value);
    const nextVacancyId = extractAvitoVacancyId(value);
    if (!nextVacancyId || nextVacancyId !== avitoVacancyId) {
      setAvitoVacancyId(null);
      setAvitoLinkedVacancy(null);
    }
  };

  const avitoVacancyPreviewMeta = avitoLinkedVacancy
    ? [
        avitoVacancyId ? `ID ${avitoVacancyId}` : null,
        avitoLinkedVacancy.workFormat,
        avitoLinkedVacancy.workSchedule,
      ].filter(Boolean).join(' · ')
    : '';

  const handleEditAvitoVacancyLink = () => {
    setAvitoLinkedVacancy(null);
  };

  // Точка входа: пользователь нажал кнопку публикации на HH.ru
  const handleExportClick = async () => {
    if (!vacancyId) {
      toast.error('Сначала сохраните вакансию');
      return;
    }

    // Пре-валидация полей перед экспортом на HH.ru
    const hhKeySkills = skillsInputs.flatMap(input => input.skills).filter(Boolean);
    const exportErrors = validateHhVacancyExport({
      title: jobTitle,
      descriptionPlainLength: hhDescriptionInfo.plainLength,
      descriptionHtmlLength: hhDescriptionInfo.htmlLength,
      descriptionMinLength: hhDescriptionInfo.minLength,
      descriptionMaxLength: hhDescriptionInfo.maxLength,
      keySkills: hhKeySkills,
    });
    if (exportErrors.length > 0) {
      toast.error(exportErrors.join('\n'), { duration: 6000 });
      return;
    }

    // Сбрасываем override-ы предыдущего запуска
    setExportSpecOverride(null);
    setExportCityOverride(null);

    // Шаг 1: валидация специализации (TASKNEIROKLYUCH-678).
    // пустая = модалка, больше 1 = модалка, ровно 1 не-лист = модалка, ровно 1 лист = ОК
    let needSpecModal = false;
    if (specialization.length === 0) {
      needSpecModal = true;
    } else if (specialization.length > 1) {
      needSpecModal = true;
    } else {
      const isLeaf = await isSpecializationLeafId(specialization[0]);
      if (!isLeaf) needSpecModal = true;
    }
    if (needSpecModal) {
      setIsExportSpecModalOpen(true);
      return;
    }

    // Шаг 2: валидация географии (TASKNEIROKLYUCH-678).
    // пустая = модалка, больше 1 = модалка, ровно 1 область/страна = модалка, ровно 1 город = ОК
    let needAreaModal = false;
    if (selectedCityIds.length === 0) {
      needAreaModal = true;
    } else if (selectedCityIds.length > 1) {
      needAreaModal = true;
    } else {
      const isLeaf = await isAreaLeafId(selectedCityIds[0]);
      if (!isLeaf) needAreaModal = true;
    }
    if (needAreaModal) {
      setIsExportAreaModalOpen(true);
      return;
    }

    // Всё заполнено — сразу к подтверждению
    setIsExportConfirmOpen(true);
  };

  // Callback после выбора специализации для экспорта
  const handleExportSpecSelected = async (specs: string[]) => {
    if (specs.length === 0) return;
    setExportSpecOverride(specs[0]);
    setIsExportSpecModalOpen(false);

    // Переходим к проверке географии (TASKNEIROKLYUCH-678).
    // пустая = модалка, больше 1 = модалка, ровно 1 область/страна = модалка, ровно 1 город = ОК
    let needAreaModal = false;
    if (selectedCityIds.length === 0) {
      needAreaModal = true;
    } else if (selectedCityIds.length > 1) {
      needAreaModal = true;
    } else {
      const isLeaf = await isAreaLeafId(selectedCityIds[0]);
      if (!isLeaf) needAreaModal = true;
    }
    if (needAreaModal) {
      setIsExportAreaModalOpen(true);
      return;
    }

    setIsExportConfirmOpen(true);
  };

  // Callback после выбора города для экспорта
  const handleExportCitySelected = (areas: string[]) => {
    if (areas.length === 0) return;
    setExportCityOverride(areas[0]);
    setIsExportAreaModalOpen(false);
    setIsExportConfirmOpen(true);
  };

  // Подтверждение публикации
  const handleExportConfirm = () => {
    setIsExportConfirmOpen(false);
    handleExportToHH();
  };

  // Экспорт вакансии на HH.ru (публикация / обновление)
  const handleExportToHH = async () => {
    if (!vacancyId) {
      toast.error('Сначала сохраните вакансию');
      return;
    }

    setHhExportLoading(true);
    try {
      const skillGroups = skillsInputs
        .filter(input => input.skills.length > 0)
        .map(input => ({ type: input.type, skills: input.skills }));

      // Используем override-ы из экспорт-флоу если они заданы
      const specForExport = exportSpecOverride
        ? extractLastPathSegment(exportSpecOverride)
        : (specialization.length > 0
          ? Array.from(new Set(specialization.map((v) => extractLastPathSegment(String(v))).filter(Boolean))).join(',')
          : '');
      const cityForExport = exportCityOverride || officeCityId || (selectedCityIds.length === 1 ? selectedCityIds[0] : '');

      const positionData: Record<string, any> = {
        title: normalizeHhVacancyTitle(jobTitle),
        description: jobDescription,
        companyDescription,
        socialPackageText,
        workFormat,
        workSchedule,
        officeCityId: cityForExport,
        cities: selectedCityIds,
        totalExperience: totalExperience.length > 0 ? totalExperience.join(',') : '',
        employmentContractTypes,
        specialization: specForExport,
        salaryFrom: hasNda ? null : (salaryFrom ? parseInt(parseFormattedNumber(salaryFrom)) : null),
        salaryTo: hasNda ? null : (salaryTo ? parseInt(parseFormattedNumber(salaryTo)) : null),
        salaryGross: hasNda ? null : salaryGross,
        salaryFrequency: salaryFrequency || undefined,
        salaryCurrency: salaryCurrency || 'RUR',
        workingHours: workingHours || undefined,
        skillGroups: skillGroups.length > 0 ? JSON.stringify(skillGroups) : '',
        officeLocations: metroEntries
          .filter(e => e.stationId || e.address || e.hhAddressId)
          .map(e => ({ metroStationId: e.stationId || undefined, address: e.address || undefined, hhAddressId: e.hhAddressId || undefined })),
      };

      let result;
      if (hhVacancyId) {
        result = await updateHHVacancyExport(hhVacancyId, positionData);
      } else {
        result = await exportVacancyToHH(positionData);
      }

      // Сохраняем hhVacancyId после экспорта и выбираем как основную
      if (result.hhVacancyId) {
        const newId = result.hhVacancyId;
        setHhVacancyId(newId);
        await apiService.updatePosition(vacancyId, { hhVacancyId: newId });
        // Добавляем новую вакансию в dropdown если её там ещё нет
        setHhVacancies(prev => {
          if (prev.some(v => v.id === newId)) return prev;
          return [...prev, { id: newId, name: jobTitle || 'Новая вакансия HH' }];
        });
      }

      // Предупреждения
      if (result.warnings && result.warnings.length > 0) {
        toast(result.warnings.join('\n'), { icon: '⚠️', duration: 5000 });
      }

      toast.success(`Вакансия ${hhVacancyId ? 'обновлена' : 'опубликована'} на HH.ru`);
    } catch (error: any) {
      const data = error?.response?.data;
      if (data?.validationErrors && data.validationErrors.length > 0) {
        toast.error(data.validationErrors.join('\n'), { duration: 6000 });
      } else {
        const rawError = data?.error;
        const message = String(
          (typeof rawError === 'object' ? rawError?.message : rawError)
          || data?.message || error?.message || 'Ошибка при экспорте на HH.ru'
        );
        if (message.includes('авторизо') || message.includes('auth') || message.includes('Forbidden')) {
          toast.error('Необходимо повторно авторизоваться в HH.ru');
        } else {
          toast.error(message);
        }
      }
    } finally {
      setHhExportLoading(false);
    }
  };

  // FR-E2E-6: Управление привязками вакансий из любых источников
  const handleAddSourceLink = async () => {
    const trimmedId = sourceLinkVacancyId.trim();
    if (!trimmedId || !sourceLinkSource) return;
    // Режим создания — копим привязки локально, отправим после создания вакансии
    if (!vacancyId) {
      setPendingSourceLinks(prev => [
        ...prev,
        { tempId: Date.now(), source: sourceLinkSource, externalVacancyId: trimmedId },
      ]);
      setSourceLinkVacancyId('');
      setSourceLinkVacancyTitle('');
      toast.success('Привязка будет добавлена после создания вакансии');
      return;
    }
    setSourceLinkLoading(true);
    try {
      const newLink = await apiService.addSourceLink(vacancyId, sourceLinkSource, trimmedId);
      setSourceLinks(prev => [...prev, newLink]);
      setSourceLinkVacancyId('');
      setSourceLinkVacancyTitle('');
      toast.success('Привязка добавлена');
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Ошибка при добавлении привязки';
      toast.error(msg);
    } finally {
      setSourceLinkLoading(false);
    }
  };

  const handleDeleteSourceLink = async (linkId: number) => {
    if (!vacancyId) return;
    try {
      await apiService.deleteSourceLink(vacancyId, linkId);
      setSourceLinks(prev => prev.filter(l => l.id !== linkId));
      toast.success('Привязка удалена');
    } catch (error: any) {
      toast.error('Ошибка при удалении привязки');
    }
  };

  const handleDeletePendingSourceLink = (tempId: number) => {
    setPendingSourceLinks(prev => prev.filter(l => l.tempId !== tempId));
  };

  const handleVacancySave = async (options?: { silent?: boolean }): Promise<boolean> => {
    const stripHtmlForValidation = (html: string) => {
      if (!html) return '';
      const div = document.createElement('div');
      div.innerHTML = html;
      return (div.textContent || div.innerText || '').trim();
    };

    const errors: {
      jobTitle?: boolean;
      jobDescription?: boolean;
      companyDescription?: boolean;
      socialPackageText?: boolean;
      salary?: boolean;
    } = {};
    let hasErrors = false;
    const errorMessages: string[] = [];

    // --- Название внутренней вакансии: обязательно, не более системного лимита ---
    const normalizedInternalTitle = normalizeInternalVacancyTitle(jobTitle);
    const internalTitleError = validateInternalVacancyTitle(jobTitle);
    if (!normalizedInternalTitle.trim()) {
      errors.jobTitle = true;
      hasErrors = true;
    } else if (internalTitleError) {
      errors.jobTitle = true;
      hasErrors = true;
      errorMessages.push(internalTitleError);
    }

    // --- Описание вакансии: обязательно ---
    if (!stripHtmlForValidation(jobDescription)) {
      errors.jobDescription = true;
      hasErrors = true;
    }

    // --- О компании: обязательно ---
    if (!stripHtmlForValidation(companyDescription)) {
      errors.companyDescription = true;
      hasErrors = true;
    }

    const skillGroupsForValidation = skillsInputs
      .filter(input => input.skills.length > 0)
      .map(input => ({ type: input.type, skills: input.skills }));
    const stringLimitErrors = validateInternalVacancyStringLimits({
      description: jobDescription,
      companyDescription,
      socialPackageText,
      internalTag,
      topics: skillsInputs.flatMap(input => input.skills),
      skillGroups: skillGroupsForValidation.length > 0
        ? JSON.stringify(skillGroupsForValidation)
        : '',
      officeLocations: metroEntries.map(entry => ({ address: entry.address })),
    });
    if (stringLimitErrors.length > 0) {
      hasErrors = true;
      errorMessages.push(...stringLimitErrors);
      if (stringLimitErrors.some(error => error.startsWith('Описание вакансии'))) {
        errors.jobDescription = true;
      }
      if (stringLimitErrors.some(error => error.startsWith('Описание компании'))) {
        errors.companyDescription = true;
      }
      if (stringLimitErrors.some(error => error.startsWith('Социальный пакет'))) {
        errors.socialPackageText = true;
      }
    }

    // --- Зарплата «до»: обязательно ---
    if (!salaryTo || !salaryTo.trim()) {
      errors.salary = true;
      hasErrors = true;
    }

    // --- Зарплата: если указаны оба, from ≤ to, оба ≥ 0 ---
    const parsedFrom = salaryFrom ? parseInt(parseFormattedNumber(salaryFrom)) : null;
    const parsedTo = salaryTo ? parseInt(parseFormattedNumber(salaryTo)) : null;
    if (parsedFrom !== null && parsedFrom < 0) {
      errors.salary = true;
      hasErrors = true;
      errorMessages.push('Зарплата «от» не может быть отрицательной');
    }
    if (parsedTo !== null && parsedTo < 0) {
      errors.salary = true;
      hasErrors = true;
      errorMessages.push('Зарплата «до» не может быть отрицательной');
    }
    if (parsedFrom !== null && parsedTo !== null && parsedFrom > parsedTo) {
      errors.salary = true;
      hasErrors = true;
      errorMessages.push('Зарплата «от» не может превышать «до»');
    }

    setValidationErrors(errors);

    if (hasErrors) {
      if (errorMessages.length > 0) {
        toast.error(errorMessages.join('\n'), { duration: 6000 });
      } else {
        toast.error('Пожалуйста, заполните все обязательные поля');
      }
      return false;
    }

    try {
      setIsSaving(true);

      // Refetch for_dialog right before save. loadedAiRequirementsFullRef captures
      // the value at page-load time, but the embedded "Настройка рассылки" tab can
      // mutate for_dialog (mandatory/optional split) without the form knowing —
      // without this refetch, the outer save would write back the stale snapshot
      // and silently revert the recruiter's mailings-tab edits.
      let latestForDialog: any = undefined;
      if (vacancyId) {
        try {
          const fresh = await apiService.getPosition(vacancyId);
          const raw = (fresh as any)?.aiRequirementsWeighted;
          if (raw) {
            const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
            if (parsed && typeof parsed === 'object') {
              latestForDialog = parsed.for_dialog;
            }
          }
        } catch (e) {
          console.warn('Failed to refetch for_dialog before vacancy save:', e);
        }
      }

      const apiLevel = (levelMapReverse as any)[level] || API_LEVEL_FALLBACK;
      const answerTime = parseInt(timePerAnswer) * 60;

      const allTopics = skillsInputs.flatMap(input => input.skills);
      const skillGroups = skillsInputs
        .filter(input => input.skills.length > 0)
        .map(input => ({
          type: input.type,
          skills: input.skills
        }));
      
      const vacancyData: any = {
        title: normalizedInternalTitle,
        description: jobDescription,
        topics: allTopics,
        skillGroups: skillGroups.length > 0 ? JSON.stringify(skillGroups) : '',
        minScore: minScore,
        level: apiLevel as PositionLevelEnum,
        answerTime: answerTime,
        status: status,
        questionsCount: questionCount,
        industry: industry.length > 0 ? industry.join(',') : '',
        // Сохраняем полные path key (например "11/10") вместо голых ID,
        // чтобы избежать коллизий между ID категорий и ролей в HH API
        specialization: specialization.length > 0
          ? specialization.filter(Boolean).join(',')
          : '',
        avitoSpecialization: avitoSpecialization.length > 0
          ? avitoSpecialization.filter(Boolean).join(',')
          : '',
        cities: selectedCityIds,
        avitoLocationIds: selectedAvitoLocationIds,
        strictRegionSearch: strictRegionSearch,
        candidateStatuses,
        experienceInPosition: experienceInPosition.length > 0 ? experienceInPosition.join(',') : '',
        experiencePeriod,
        totalExperience: totalExperience.length > 0 ? totalExperience.join(',') : '',
        excludeJumpers,
        filterByExpIndustry,
        salaryFrom: salaryFrom ? parseInt(parseFormattedNumber(salaryFrom)) : null,
        salaryTo: salaryTo ? parseInt(parseFormattedNumber(salaryTo)) : null,
        education: Array.isArray(education) && education.length > 0 ? education.join(',') : '',
        ageFrom: ageFrom ? parseInt(ageFrom) : null,
        ageTo: ageTo ? parseInt(ageTo) : null,
        includeNoAge,
        gender,
        citizenship,
        companyDescription,
        workFormat,
        workSchedule,
        officeCityId: officeCityId || '',
        officeLocations: metroEntries
          .filter(e => e.stationId || e.address || e.hhAddressId)
          .map(e => ({ metroStationId: e.stationId || undefined, address: e.address || undefined, hhAddressId: e.hhAddressId || undefined })),
        useMetroInSearch,
        employmentContractTypes,
        socialPackageText,
        relocationLocation,
        relocationAssistance,
        hasNda,
        hasBonuses,
        salarySpecified,
        salaryGross,
        salaryFrequency: salaryFrequency || null,
        salaryCurrency: salaryCurrency || 'RUR',
        workingHours: workingHours || null,
        internalTag,
        hhVacancyId: hhVacancyId || '',  // Send empty string to clear (MapStruct ignores null)
        avitoVacancyId: avitoVacancyId || '',
        hhAdditionalVacancyIds: hhAdditionalVacancyIds,  // Send empty array to clear
        aiScreeningEnabled: isAiScreeningEnabled,
        hrInterviewEnabled: isHrInterviewEnabled,
        scoringEnabled: isScoringEnabled,
        ...(() => {
          // Use external requirements with weights (from CreateVacancyPage)
          const reqSource = aiRequirementsWithWeights || internalRequirements;
          if (reqSource && (reqSource.required.length > 0 || reqSource.optional.length > 0)) {
            const requiredList = reqSource.required.filter(r => r.text).map(r => ({ text: r.text, weight: r.weight ?? 7 }));
            const optionalList = reqSource.optional.filter(r => r.text).map(r => ({ text: r.text, weight: r.weight ?? 3 }));
            const currentForDialog = {
              required: requiredList.map(r => r.text),
              optional: optionalList.map(r => r.text),
            };
            // for_dialog is managed on "Настройка рассылки" page, so normally we
            // preserve it. If the recruiter edited candidate requirements in the
            // vacancy form itself, the dialog requirements must follow the current
            // vacancy requirements instead of keeping the old AI-generated snapshot.
            const existingForDialog = latestForDialog !== undefined
              ? latestForDialog
              : loadedAiRequirementsFullRef.current?.for_dialog;
            let forDialog: any;
            if (requirementsEditedInVacancyFormRef.current || !vacancyId) {
              forDialog = currentForDialog;
            } else if (
              existingForDialog &&
              typeof existingForDialog === 'object' &&
              !Array.isArray(existingForDialog)
            ) {
              forDialog = existingForDialog;
            } else if (Array.isArray(existingForDialog) && existingForDialog.length > 0) {
              forDialog = existingForDialog.filter((x: any) => typeof x === 'string' && x.trim());
            } else {
              forDialog = currentForDialog;
            }
            return {
              aiRequirementsWeighted: JSON.stringify({
                required: requiredList,
                optional: optionalList,
                for_dialog: forDialog,
              }),
            };
          }
          return {};
        })(),
      };

      let resultVacancyId: number;

      if (vacancyId) {
        await apiService.updatePosition(vacancyId, vacancyData);
        resultVacancyId = vacancyId;
        if (!options?.silent) {
          toast.success('Вакансия успешно обновлена');
        }
      } else {
        const newVacancy = await apiService.createPosition(vacancyData);
        resultVacancyId = newVacancy.id;
        toast.success('Вакансия успешно создана');

        // Отправляем накопленные ATS-привязки после успешного создания вакансии
        if (pendingSourceLinks.length > 0) {
          for (const link of pendingSourceLinks) {
            try {
              await apiService.addSourceLink(resultVacancyId, link.source, link.externalVacancyId);
            } catch (e: any) {
              const msg = e?.response?.data?.message || e?.message || 'Ошибка при добавлении привязки';
              toast.error(`${link.source} ${link.externalVacancyId}: ${msg}`);
            }
          }
          setPendingSourceLinks([]);
        }
      }

      if (vacancyId) {
        await loadVacancyData();
      }

      if (!options?.silent) {
        if (onSaveSuccess) {
          onSaveSuccess(resultVacancyId);
        } else {
          onBack();
        }
      }
      return true;
    } catch (error: any) {
      console.error('Ошибка сохранения вакансии:', error);
      const data = error?.response?.data;
      const backendErrors: string[] | undefined = data?.errors;
      const backendMessage: string | undefined = data?.message;
      if (backendErrors && backendErrors.length > 0) {
        toast.error(backendErrors.join('\n'), { duration: 6000 });
      } else {
        toast.error(backendMessage || (vacancyId ? 'Ошибка при сохранении вакансии' : 'Ошибка при создании вакансии'));
      }
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const addSkillToInput = (inputId: string) => {
    setSkillsInputs(prev => prev.map(input => {
      if (input.id === inputId) {
        const parts = input.inputValue
          .split(',')
          .map((part) => part.trim())
          .filter((part) => part.length > 0);

        if (parts.length === 0) {
          return input;
        }

        const uniqueToAdd = parts.filter((topic) => !input.skills.includes(topic));
        if (uniqueToAdd.length === 0) {
          return { ...input, inputValue: '' };
        }

        return { ...input, skills: [...input.skills, ...uniqueToAdd], inputValue: '' };
      }
      return input;
    }));
  };

  const removeSkillFromInput = (inputId: string, skill: string) => {
    setSkillsInputs(prev => prev.map(input => {
      if (input.id === inputId) {
        const updatedSkills = input.skills.filter(s => s !== skill);
        const updatedSkillExperience = { ...(input.skillExperience || {}) };
        delete updatedSkillExperience[skill];
        return { 
          ...input, 
          skills: updatedSkills,
          skillExperience: input.type === 'all' ? updatedSkillExperience : input.skillExperience
        };
      }
      return input;
    }));
  };

  const updateInputValue = (inputId: string, value: string) => {
    setSkillsInputs(prev => prev.map(input => {
      if (input.id === inputId) {
        return { ...input, inputValue: value };
      }
      return input;
    }));
  };

  const toggleInputType = (inputId: string) => {
    setSkillsInputs(prev => prev.map(input => {
      if (input.id === inputId) {
        const newType = input.type === 'any' ? 'all' : 'any';
        return { 
          ...input, 
          type: newType,
          skillExperience: newType === 'all' ? {} : undefined,
          groupExperience: newType === 'any' ? '' : undefined
        };
      }
      return input;
    }));
  };

  const addNewSkillsInput = () => {
    const newId = String(Date.now());
    setSkillsInputs(prev => [...prev, { id: newId, type: 'any', skills: [], inputValue: '', skillExperience: {}, groupExperience: '' }]);
  };

  const removeSkillsInput = (inputId: string) => {
    if (skillsInputs.length > 1) {
      setSkillsInputs(prev => prev.filter(input => input.id !== inputId));
    }
  };

  const toggleCandidateStatus = (status: string) => {
    setCandidateStatuses(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const toggleContractType = (type: string) => {
    setEmploymentContractTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const selectedCitizenshipNames = citizenship
    .map((id) => countries.find((country) => country.id === id)?.name || id)
    .join(', ');

  if (isLoading && vacancyId) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#f5f6f1]">
        <div className="text-lg">Загрузка данных вакансии...</div>
      </div>
    );
  }

  if (isAiGenerating) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#f5f6f1]">
        <div className="text-lg">Генерация данных вакансии...</div>
      </div>
    );
  }

  const isEditMode = !!vacancyId;
  const hasAtsIntegration = atsSourceOptions.length > 0;
  const shouldShowAtsSourceLinksBlock = hasAtsIntegration || (isEditMode && (sourceLinks.length > 0 || pendingSourceLinks.length > 0));

  const handleTabChange = (tab: VacancyEditorTab) => {
    if (!vacancyId) {
      if (tab === 'interview') {
        toast.error('Сначала создайте вакансию, чтобы настроить интервью');
        return;
      }
      if (tab === 'dialogRequirements') {
        toast.error('Сначала создайте вакансию, чтобы настроить рассылку');
        return;
      }
    }
    setActiveTab(tab);
  };

  const isInterviewMode = activeTab === 'interview' && vacancyId;
  const isDialogRequirementsMode = activeTab === 'dialogRequirements';

  const closeAfterSave = (savedVacancyId: number) => {
    if (onSaveSuccess) {
      setIsClosing(true);
      setTimeout(() => {
        void onSaveSuccess(savedVacancyId);
      }, 220);
      return;
    }
    handleCancelClick();
  };

  const handlePrimaryAction = async () => {
    if (isInterviewMode) {
      setIsInterviewSaving(true);
      try {
        const interviewForm = interviewFormRef.current;
        if (!interviewForm) {
          return;
        }
        if (!interviewForm.isReady()) {
          toast.error('Данные AI-скрининга ещё загружаются. Дождитесь завершения загрузки.');
          return;
        }
        const interviewSaved = await interviewForm.save();
        if (!interviewSaved) {
          return;
        }
        if (vacancyId) {
          closeAfterSave(vacancyId);
        }
      } finally {
        setIsInterviewSaving(false);
      }
    } else if (!vacancyId && onBeforeCreate && !requirementsReviewed) {
      const missing: string[] = [];
      if (!jobDescription || jobDescription.trim().length === 0) missing.push('Описание вакансии');
      if (!companyDescription || companyDescription.trim().length === 0) missing.push('О компании и команде');
      if (!salaryFrom && !salaryTo) missing.push('Зарплата');
      if (!workFormat || workFormat.trim().length === 0) missing.push('Формат работы');
      if (!experienceInPosition || experienceInPosition.length === 0) missing.push('Опыт в должности');
      if (missing.length > 0) {
        toast.error(
          `Заполните обязательные поля перед генерацией требований: ${missing.join(', ')}`,
          { duration: 5000 }
        );
        return;
      }

      const formData = {
        title: jobTitle,
        jobDescription,
        skills: skillsInputs.flatMap((g: any) => g.skills || []).join(', '),
        companyDescription,
        level,
        experienceInPosition,
        salaryFrom,
        salaryTo,
      };
      await onBeforeCreate(formData);
      onRequirementsReviewed?.();
    } else if (
      vacancyId &&
      activeTab === 'vacancy' &&
      interviewFormRef.current
    ) {
      const interviewForm = interviewFormRef.current;
      if (!interviewForm.isReady()) {
        toast.error('Данные AI-скрининга ещё загружаются. Дождитесь завершения загрузки.');
        return;
      }
      setIsSaving(true);
      try {
        const vacancySaved = await handleVacancySave({ silent: true });
        if (!vacancySaved) {
          return;
        }
        const interviewSaved = await interviewForm.save();
        if (!interviewSaved) {
          toast.error(
            'Вакансия сохранена. Вопросы AI-скрининга не сохранились — проверьте вкладку «Настройка AI скрининга».'
          );
          return;
        }
        closeAfterSave(vacancyId);
      } finally {
        setIsSaving(false);
      }
    } else {
      if (isDialogRequirementsMode && vacancyId && mailingsSaveRef.current) {
        await mailingsSaveRef.current();
        return;
      }
      await handleVacancySave();
    }
  };

  const isWaitingInterviewForm =
    !!vacancyId &&
    (activeTab === 'vacancy' || Boolean(isInterviewMode)) &&
    !isInterviewFormReady;
  const primaryButtonDisabled =
    isSaving || isInterviewSaving || isClosing || isWaitingInterviewForm;

  const primaryButtonLabel = (() => {
    if (isSaving || isInterviewSaving) {
      return 'Сохранение...';
    }
    if (isWaitingInterviewForm) {
      return 'Загрузка...';
    }
    if (!vacancyId && onBeforeCreate && !requirementsReviewed) {
      return 'Далее: требования';
    }
    return isEditMode ? 'Сохранить' : 'Создать вакансию';
  })();

  const primaryButtonTestId = (() => {
    if (!vacancyId && onBeforeCreate && !requirementsReviewed) {
      return 'vacancy-next-requirements-button';
    }
    if (!vacancyId) {
      return 'vacancy-create-button';
    }
    return isEditMode ? 'vacancy-save-button' : 'vacancy-create-button';
  })();

  const hhVacancyTitleLength = getHhVacancyTitleLength(jobTitle);
  const internalVacancyTitleLength = getInternalVacancyTitleLength(jobTitle);

  return (
    <div
      ref={formContainerRef}
      className={`bg-[#f5f6f1] pb-4 transition-all duration-200 ease-out ${isClosing ? 'opacity-0 -translate-y-2 pointer-events-none' : 'opacity-100 translate-y-0'}`}
    >
      <div className="w-full max-w-full px-6">
        <div className="sticky top-0 z-30 flex justify-between items-center bg-[#f5f6f1] -mx-6 px-6 py-4 mb-6">
          <ContentTabToggle
            options={[
              { id: 'vacancy', label: 'Настройка вакансии' },
              { id: 'dialogRequirements', label: 'Настройка рассылки' },
              { id: 'interview', label: 'Настройка AI скрининга' },
            ]}
            activeTab={activeTab}
            onTabChange={(tab) => handleTabChange(tab as VacancyEditorTab)}
          />
          <div className="flex gap-3">
            {isDialogRequirementsMode && isMailingStopAvailable && (
              <button
                type="button"
                onClick={handleMailingStop}
                disabled={primaryButtonDisabled}
                className="px-8 h-10 rounded-[12px] font-inter text-white font-medium bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Остановить
              </button>
            )}
            {isDialogRequirementsMode && isMailingStartAvailable && (
              <button
                type="button"
                onClick={handleMailingStart}
                disabled={primaryButtonDisabled}
                className="px-8 h-10 rounded-[12px] font-inter text-white font-medium bg-green-600 hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Запустить
              </button>
            )}
            <button
              data-testid="vacancy-cancel-button"
              onClick={handleCancelClick}
              disabled={isClosing}
              className="px-8 h-10 rounded-[12px] font-inter border border-[#e2e4e9] bg-white hover:bg-[#f6f8fa] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Отмена
            </button>
            <button
              data-testid={primaryButtonTestId}
              onClick={handlePrimaryAction}
              disabled={primaryButtonDisabled}
              className="px-8 h-10 rounded-[12px] font-inter text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: colors.primary }}
              onMouseEnter={(e) => {
                if (!primaryButtonDisabled) (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.primaryHover;
              }}
              onMouseLeave={(e) => {
                if (!primaryButtonDisabled) (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.primary;
              }}
            >
              {primaryButtonLabel}
            </button>
          </div>
        </div>

        {/* Flex container: form content (A) + AI sidebar (B) */}
        <div ref={flexContainerRef} className="flex gap-0 items-start">
          {/* Container A: form content */}
          <div className="flex-1 min-w-0">
        {vacancyId && (
          <div className={isInterviewMode ? 'ai-fade-in' : ''} style={{ display: isInterviewMode ? undefined : 'none' }}>
            <InterviewQuestionsEditForm
              ref={interviewFormRef}
              vacancyId={vacancyId}
              onBack={onBack}
              onSaveSuccess={onSaveSuccess}
              hideActions
              onSavingChange={setIsInterviewSaving}
              onReadyChange={setIsInterviewFormReady}
            />
          </div>
        )}
          {/* Dialog Requirements tab — reuses the mailings page (list → detail, start/save) */}
          <div
            className={`pt-3 ${isDialogRequirementsMode ? 'ai-fade-in' : ''}`}
            style={{ display: isDialogRequirementsMode ? undefined : 'none' }}
          >
            {isDialogRequirementsMode && vacancyId && (
              <MailingsPage
                embedded
                externalSaveRef={mailingsSaveRef}
                externalStartRef={mailingsStartRef}
                externalStopRef={mailingsStopRef}
                onStartAvailableChange={setIsMailingStartAvailable}
                onStopAvailableChange={setIsMailingStopAvailable}
              />
            )}
          </div>
          <div className={`rounded-[12px] relative ${!isInterviewMode && !isDialogRequirementsMode ? 'ai-fade-in' : ''}`} style={{ display: (isInterviewMode || isDialogRequirementsMode) ? 'none' : undefined }}>
          {/* Действие после диалога */}
          <div className="flex flex-col gap-3 mb-4">
            <div className="flex items-center gap-2">
              <span className="text-[16px] font-medium leading-[1.5] tracking-[-0.011em] text-[#0a0d14]">
                Действие после диалога
              </span>
              <InfoCircleIcon className="w-5 h-5 text-[#868c98] flex-shrink-0" />
            </div>

            <div className="flex gap-6">
              <div className="flex-1 bg-white rounded-[16px] p-4 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-[14px] bg-[#f6f8fa] flex items-center justify-center">
                    <AiScreeningActionIcon className="w-5 h-5 text-[#525866]" />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (!isAiScreeningEnabled && interviewFormRef.current?.getQuestionCount() === 0) {
                        toast.error('Сначала настройте вопросы для интервью');
                        return;
                      }
                      setIsAiScreeningEnabled(prev => !prev);
                    }}
                    aria-pressed={isAiScreeningEnabled}
                    className="relative inline-flex items-center focus:outline-none"
                    style={{
                      width: 38,
                      height: 24,
                      borderRadius: 9999,
                      background: isAiScreeningEnabled ? '#E16349' : '#E5E7EB',
                      boxShadow: isAiScreeningEnabled
                        ? 'inset 0px 4.8px 4.8px 0px rgba(15, 15, 16, 0.12)'
                        : 'inset 0px 2px 4px 0px rgba(15, 15, 16, 0.08)',
                      transition: 'background 150ms ease'
                    }}
                  >
                    <span
                      className="absolute"
                      style={{
                        left: isAiScreeningEnabled ? 19 : 3,
                        top: 5,
                        width: 14,
                        height: 14,
                        borderRadius: 9999,
                        backgroundColor: '#FFFFFF',
                        boxShadow:
                          '0px 2.4px 4.8px 0px rgba(22, 38, 100, 0.08), 0px 4.8px 9.6px 0px rgba(22, 38, 100, 0.08), 0px 7.2px 12px 0px rgba(22, 38, 100, 0.08), inset 0px -3.6px 3.6px 0px rgba(228, 229, 231, 1)',
                        transition: 'left 150ms ease'
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          left: 5,
                          top: 5,
                          width: 4,
                          height: 4,
                          borderRadius: 9999,
                          backgroundColor: isAiScreeningEnabled ? '#E16349' : '#CDD0D5',
                          boxShadow: isAiScreeningEnabled
                            ? '0px 2.4px 4.8px 0px rgba(27, 28, 29, 0.04)'
                            : 'none',
                          transition: 'background-color 150ms ease'
                        }}
                      />
                    </span>
                  </button>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[14px] font-medium leading-[1.43] tracking-[-0.006em] text-[#0a0d14]">
                    AI-скрининг
                  </span>
                  <span className="text-[14px] font-normal leading-[1.43] tracking-[-0.006em] text-[#868c98]">
                    Бот отправит ссылку на автоматическое интервью с ИИ
                  </span>
                </div>
              </div>

              <div className="flex-1 bg-white rounded-[16px] p-4 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-[14px] bg-[#f6f8fa] flex items-center justify-center">
                    <HrInterviewActionIcon className="w-5 h-5 text-[#525866]" />
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsHrInterviewEnabled(prev => !prev)}
                    aria-pressed={isHrInterviewEnabled}
                    className="relative inline-flex items-center focus:outline-none"
                    style={{
                      width: 38,
                      height: 24,
                      borderRadius: 9999,
                      background: isHrInterviewEnabled ? '#E16349' : '#E5E7EB',
                      boxShadow: isHrInterviewEnabled
                        ? 'inset 0px 4.8px 4.8px 0px rgba(15, 15, 16, 0.12)'
                        : 'inset 0px 2px 4px 0px rgba(15, 15, 16, 0.08)',
                      transition: 'background 150ms ease'
                    }}
                  >
                    <span
                      className="absolute"
                      style={{
                        left: isHrInterviewEnabled ? 19 : 3,
                        top: 5,
                        width: 14,
                        height: 14,
                        borderRadius: 9999,
                        backgroundColor: '#FFFFFF',
                        boxShadow:
                          '0px 2.4px 4.8px 0px rgba(22, 38, 100, 0.08), 0px 4.8px 9.6px 0px rgba(22, 38, 100, 0.08), 0px 7.2px 12px 0px rgba(22, 38, 100, 0.08), inset 0px -3.6px 3.6px 0px rgba(228, 229, 231, 1)',
                        transition: 'left 150ms ease'
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          left: 5,
                          top: 5,
                          width: 4,
                          height: 4,
                          borderRadius: 9999,
                          backgroundColor: isHrInterviewEnabled ? '#E16349' : '#CDD0D5',
                          boxShadow: isHrInterviewEnabled
                            ? '0px 2.4px 4.8px 0px rgba(27, 28, 29, 0.04)'
                            : 'none',
                          transition: 'background-color 150ms ease'
                        }}
                      />
                    </span>
                  </button>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[14px] font-medium leading-[1.43] tracking-[-0.006em] text-[#0a0d14]">
                    HR-интервью
                  </span>
                  <span className="text-[14px] font-normal leading-[1.43] tracking-[-0.006em] text-[#868c98]">
                    Бот отправит ссылку для записи на собеседование с HR
                  </span>
                </div>
              </div>
            </div>

            {/* Бегунок оценки резюме (скоринг) */}
            <div className="bg-white rounded-[16px] p-4 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-[14px] bg-[#f6f8fa] flex items-center justify-center">
                  <svg className="w-5 h-5 text-[#525866]" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 2L12.09 7.26L18 8.27L14 12.14L14.18 18.02L10 15.77L5.82 18.02L6 12.14L2 8.27L7.91 7.26L10 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <button
                  type="button"
                  onClick={() => setIsScoringEnabled(prev => !prev)}
                  aria-pressed={isScoringEnabled}
                  className="relative inline-flex items-center focus:outline-none"
                  style={{
                    width: 38,
                    height: 24,
                    borderRadius: 9999,
                    background: isScoringEnabled ? '#E16349' : '#E5E7EB',
                    boxShadow: isScoringEnabled
                      ? 'inset 0px 4.8px 4.8px 0px rgba(15, 15, 16, 0.12)'
                      : 'inset 0px 2px 4px 0px rgba(15, 15, 16, 0.08)',
                    transition: 'background 150ms ease'
                  }}
                >
                  <span
                    className="absolute"
                    style={{
                      left: isScoringEnabled ? 19 : 3,
                      top: 5,
                      width: 14,
                      height: 14,
                      borderRadius: 9999,
                      backgroundColor: '#FFFFFF',
                      boxShadow:
                        '0px 2.4px 4.8px 0px rgba(22, 38, 100, 0.08), 0px 4.8px 9.6px 0px rgba(22, 38, 100, 0.08), 0px 7.2px 12px 0px rgba(22, 38, 100, 0.08), inset 0px -3.6px 3.6px 0px rgba(228, 229, 231, 1)',
                      transition: 'left 150ms ease'
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        left: 5,
                        top: 5,
                        width: 4,
                        height: 4,
                        borderRadius: 9999,
                        backgroundColor: isScoringEnabled ? '#E16349' : '#CDD0D5',
                        boxShadow: isScoringEnabled
                          ? '0px 2.4px 4.8px 0px rgba(27, 28, 29, 0.04)'
                          : 'none',
                        transition: 'background-color 150ms ease'
                      }}
                    />
                  </span>
                </button>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[14px] font-medium leading-[1.43] tracking-[-0.006em] text-[#0a0d14]">
                  Оценка резюме
                </span>
                <span className="text-[14px] font-normal leading-[1.43] tracking-[-0.006em] text-[#868c98]">
                  AI-оценка релевантности резюме при поиске и разборе откликов
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            {/* Basic Parameters Section */}
            <div className="bg-white rounded-[12px] p-8 space-y-6">
              <h2 className="text-xl font-semibold text-[#0a0d14]">Основные параметры</h2>

              {/* Job Title */}
              <div data-field-id="title">
                <label className="block text-sm font-medium text-[#0a0d14] mb-2">
                  Название вакансии <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={jobTitle}
                  onChange={(e) => {
                    setJobTitle(e.target.value);
                    if (validationErrors.jobTitle) {
                      setValidationErrors(prev => ({ ...prev, jobTitle: false }));
                    }
                  }}
                  className={`w-full px-6 py-4 border rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-opacity-50 ${
                    validationErrors.jobTitle ? 'border-red-500 bg-red-50' : 'border-[#e2e4e9]'
                  }`}
                  placeholder="Frontend Разработчик"
                />
                {internalVacancyTitleLength > VACANCY_TITLE_MAX_LENGTH ? (
                  <p className="text-xs text-red-600 mt-1">Название вакансии не должно превышать {VACANCY_TITLE_MAX_LENGTH} символов. Сейчас: {internalVacancyTitleLength}</p>
                ) : hhVacancyTitleLength > HH_VACANCY_TITLE_MAX_LENGTH ? (
                  <p className="text-xs text-amber-600 mt-1">Для публикации на HH.ru название должно быть не длиннее {HH_VACANCY_TITLE_MAX_LENGTH} символов. Внутреннее сохранение доступно. Сейчас: {hhVacancyTitleLength}</p>
                ) : null}
                {!showInternalTagInput && (
                  <button
                    type="button"
                    onClick={() => setShowInternalTagInput(true)}
                    className="flex items-center gap-2 mt-2 text-sm cursor-pointer hover:opacity-80 transition-opacity"
                    style={{ color: colors.primary }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8.39083 1.72367C8.14084 1.47361 7.80176 1.33308 7.44816 1.33301H2.66683C2.31321 1.33301 1.97407 1.47348 1.72402 1.72353C1.47397 1.97358 1.3335 2.31272 1.3335 2.66634V7.44767C1.33357 7.80127 1.4741 8.14035 1.72416 8.39034L7.52683 14.193C7.82984 14.4941 8.23966 14.6631 8.66683 14.6631C9.094 14.6631 9.50382 14.4941 9.80683 14.193L14.1935 9.80634C14.4946 9.50333 14.6636 9.09351 14.6636 8.66634C14.6636 8.23917 14.4946 7.82935 14.1935 7.52634L8.39083 1.72367Z" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M4.99984 5.33366C5.18393 5.33366 5.33317 5.18442 5.33317 5.00033C5.33317 4.81623 5.18393 4.66699 4.99984 4.66699C4.81574 4.66699 4.6665 4.81623 4.6665 5.00033C4.6665 5.18442 4.81574 5.33366 4.99984 5.33366Z" fill="currentColor" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Добавить внутреннюю метку</span>
                  </button>
                )}
                {showInternalTagInput && (
                  <div className="mt-4 space-y-2">
                    <label className="block text-sm font-medium text-[#0a0d14]">
                      Внутренняя метка (видна только вам)
                    </label>
                    <input
                      type="text"
                      value={internalTag}
                      onChange={(e) => setInternalTag(e.target.value)}
                      placeholder="Например: Бизнес-аналитик, заказчик"
                      className="w-full px-6 py-4 border border-[#e2e4e9] rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-opacity-50"
                    />
                    <p className="text-xs text-[#868c98]">
                      Введите метку для внутреннего использования
                    </p>
                  </div>
                )}
              </div>

              {/* HH.ru Vacancy Linking */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#0a0d14] mb-2">
                    Основная вакансия <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-600 text-white ml-1">hh.ru</span>
                  </label>
                  <HHMainVacancyDropdown
                    vacancies={hhVacancies}
                    selectedId={hhVacancyId}
                    onChange={handleHHVacancySelect}
                    disabled={hhVacanciesLoading || hhImportLoading}
                    loading={hhVacanciesLoading || hhImportLoading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0a0d14] mb-2">
                    Дополнительные вакансии <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-600 text-white ml-1">hh.ru</span>
                  </label>
                  <HHAdditionalVacanciesDropdown
                    vacancies={hhVacancies.filter(v => v.id !== hhVacancyId)}
                    selectedIds={hhAdditionalVacancyIds}
                    onChange={setHhAdditionalVacancyIds}
                    disabled={hhVacanciesLoading}
                    loading={hhVacanciesLoading}
                  />
                </div>

                {/* Кнопки импорта/экспорта HH.ru */}
                <div className="flex gap-2">
                  {hhVacancyId && (
                    <button
                      type="button"
                      onClick={handleImportFromHH}
                      disabled={hhImportLoading}
                      className="px-4 py-2 rounded-xl text-sm font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-red-600 hover:bg-red-700"
                    >
                      {hhImportLoading ? 'Импорт...' : 'Импортировать из HH.ru'}
                    </button>
                  )}
                  {isEditMode && (
                    <button
                      type="button"
                      onClick={handleExportClick}
                      disabled={hhExportLoading || !jobTitle.trim()}
                      className="px-4 py-2 rounded-xl text-sm font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-red-600 hover:bg-red-700"
                    >
                      {hhExportLoading
                        ? (hhVacancyId ? 'Обновление...' : 'Публикация...')
                        : (hhVacancyId ? 'Обновить на HH.ru' : 'Опубликовать на HH.ru')}
                    </button>
                  )}
                </div>

              </div>

              {hasAvitoIntegration && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-[#0a0d14] mb-2">
                      Связанная вакансия <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-[#00a76f] text-white ml-1">Авито</span>
                    </label>
                    {avitoLinkedVacancy ? (
                      <div className="w-full px-6 py-4 border border-[#e2e4e9] rounded-xl bg-white flex items-center justify-between gap-4">
                        <div className="min-w-0 flex items-start gap-3">
                          <CheckIcon
                            className="mt-0.5 h-5 w-5 shrink-0"
                            style={{ color: colors.primary }}
                          />
                          <div className="min-w-0">
                            <div className="text-base text-[#0a0d14] truncate">
                              {avitoLinkedVacancy.title || `Вакансия Авито ${avitoVacancyId}`}
                            </div>
                            {avitoVacancyPreviewMeta && (
                              <div className="mt-1 text-sm text-[#525866] truncate">
                                {avitoVacancyPreviewMeta}
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={handleEditAvitoVacancyLink}
                          disabled={avitoImportLoading}
                          className="shrink-0 px-4 py-2 rounded-xl text-sm font-medium border border-[#e2e4e9] text-[#525866] transition-colors hover:bg-[#f6f8fa] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Изменить
                        </button>
                      </div>
                    ) : (
                      <div className="w-full px-4 py-3 border border-[#e2e4e9] rounded-xl bg-white flex flex-col sm:flex-row sm:items-center gap-3 focus-within:border-[#c9ced6]">
                        <input
                          type="text"
                          value={avitoVacancyInput}
                          onChange={(event) => handleAvitoVacancyInputChange(event.target.value)}
                          placeholder="Вставьте ссылку или ID вакансии Авито"
                          disabled={avitoLinkLoading || avitoImportLoading}
                          className="min-w-0 flex-1 bg-transparent px-2 py-1 text-base text-[#0a0d14] placeholder:text-gray-400 focus:outline-none disabled:text-gray-400"
                        />
                        <button
                          type="button"
                          onClick={handleAttachAvitoVacancy}
                          disabled={avitoLinkLoading || avitoImportLoading || !avitoVacancyInput.trim()}
                          className="shrink-0 h-10 px-4 rounded-xl text-sm font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ backgroundColor: colors.primary }}
                        >
                          {avitoLinkLoading ? 'Привязка...' : 'Привязать'}
                        </button>
                      </div>
                    )}
                  </div>

                  {avitoLinkedVacancy && avitoVacancyId && (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleImportFromAvito}
                        disabled={avitoImportLoading || avitoLinkLoading}
                        className="px-4 py-2 rounded-xl text-sm font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ backgroundColor: colors.primary }}
                      >
                        {avitoImportLoading ? 'Импорт...' : 'Импортировать из Авито'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* FR-E2E-6: Привязки вакансий из любых источников */}
              {shouldShowAtsSourceLinksBlock && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-[#0a0d14]">
                    Вакансия из ATS
                  </label>

                  {/* Список существующих привязок */}
                  {sourceLinks.length > 0 && (
                    <div className="space-y-1">
                      {sourceLinks.map(link => (
                        <div key={link.id} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg text-sm">
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold text-white ${
                              link.source === 'HH' ? 'bg-red-600' :
                              link.source === 'ESTAFF' ? 'bg-blue-600' :
                              link.source === 'SKILLAZ' ? 'bg-purple-600' : 'bg-gray-600'
                            }`}>
                              {link.source}
                            </span>
                            <span className="text-gray-700">{link.externalVacancyId}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDeleteSourceLink(link.id)}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                            title="Удалить привязку"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Список локальных (ещё не отправленных) привязок — только режим создания */}
                  {pendingSourceLinks.length > 0 && (
                    <div className="space-y-1">
                      {pendingSourceLinks.map(link => (
                        <div key={link.tempId} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg text-sm">
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold text-white ${
                              link.source === 'HH' ? 'bg-red-600' :
                              link.source === 'ESTAFF' ? 'bg-blue-600' :
                              link.source === 'SKILLAZ' ? 'bg-purple-600' : 'bg-gray-600'
                            }`}>
                              {link.source}
                            </span>
                            <span className="text-gray-700">{link.externalVacancyId}</span>
                            <span className="text-xs text-gray-400">(будет привязано после создания)</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleDeletePendingSourceLink(link.tempId)}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                            title="Удалить привязку"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {hasAtsIntegration && sourceLinkSource && (
                    <>
                      {/* Форма добавления новой ATS-привязки */}
                      <div className="flex items-end gap-2">
                        <div className="w-32">
                          <label className="block text-xs text-gray-500 mb-1">Источник</label>
                          <select
                            value={sourceLinkSource}
                            onChange={e => {
                              setSourceLinkSource(e.target.value as AtsSourceLinkSource);
                              setSourceLinkVacancyId('');
                              setSourceLinkVacancyTitle('');
                            }}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                          >
                            {atsSourceOptions.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs text-gray-500 mb-1">ID вакансии</label>
                          <AtsVacancySelect
                            source={sourceLinkSource.toLowerCase()}
                            value={sourceLinkVacancyId}
                            selectedTitle={sourceLinkVacancyTitle}
                            onChange={({ atsVacancyId, atsVacancyTitle }) => {
                              setSourceLinkVacancyId(atsVacancyId);
                              setSourceLinkVacancyTitle(atsVacancyTitle);
                            }}
                            onClear={() => {
                              setSourceLinkVacancyId('');
                              setSourceLinkVacancyTitle('');
                            }}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleAddSourceLink}
                          disabled={sourceLinkLoading || !sourceLinkSource || !sourceLinkVacancyId.trim()}
                          className="px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ backgroundColor: colors.primary, color: 'white' }}
                        >
                          {sourceLinkLoading ? '...' : 'Добавить'}
                        </button>
                      </div>
                      <p className="text-xs text-gray-400">
                        Привязка позволяет связать эту вакансию с вакансиями в доступных ATS для отслеживания
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* Job Description */}
              <div data-field-id="description">
                <label className="block text-sm font-medium text-[#0a0d14] mb-2">
                  Описание вакансии <span className="text-red-500">*</span>
                </label>
                <div className={`rounded-xl ${validationErrors.jobDescription ? 'ring-2 ring-red-500 bg-red-50' : ''}`}>
                  <RichTextEditor
                    value={jobDescription}
                    onChange={(val) => {
                      setJobDescription(val);
                      setValidationErrors((prev) => ({ ...prev, jobDescription: false }));
                    }}
                    placeholder="Системный аналитик (B2B SaaS, AI-argperator)"
                    disableLink
                  />
                </div>
                {hhDescriptionInfo.blocks?.description != null && (
                  <p className="text-xs mt-1 text-gray-400">{hhDescriptionInfo.blocks.description.length} символов</p>
                )}
              </div>

              {/* Company Description */}
              <div data-field-id="companyDescription">
                <label className="block text-sm font-medium text-[#0a0d14] mb-2">
                  О компании и команде <span className="text-red-500">*</span>
                </label>
                <div className={`rounded-xl ${validationErrors.companyDescription ? 'ring-2 ring-red-500 bg-red-50' : ''}`}>
                  <RichTextEditor
                    value={companyDescription}
                    onChange={(val) => {
                      setCompanyDescription(val);
                      setValidationErrors((prev) => ({ ...prev, companyDescription: false }));
                    }}
                    placeholder="Мы создаём платформу-агрегатор ИИ-моделей, которая позволяет бизнесу интегрировать и использовать различные AI-сервисы"
                    disableLink
                  />
                </div>
                {hhDescriptionInfo.blocks?.companyDescription != null && (
                  <p className="text-xs mt-1 text-gray-400">{hhDescriptionInfo.blocks.companyDescription.length} символов</p>
                )}
              </div>

              {/* Соц. пакет */}
              <div data-field-id="socialPackage">
                <label className="block text-sm font-medium text-[#0a0d14] mb-2">
                  Что входит в соц. пакет
                </label>
                <textarea
                  value={socialPackageText}
                  onChange={(e) => {
                    setSocialPackageText(e.target.value);
                    setValidationErrors((prev) => ({ ...prev, socialPackageText: false }));
                  }}
                  placeholder="Опишите социальный пакет вакансии (ДМС, оплата спорта, обучение за счёт компании...)"
                  rows={4}
                  className={`w-full rounded-xl p-3 text-sm text-[#0a0d14] resize-y focus:outline-none focus:ring-2 focus:border-transparent ${
                    validationErrors.socialPackageText
                      ? 'border-2 border-red-500 bg-red-50'
                      : 'border border-[#e2e4e9]'
                  }`}
                  style={{ '--tw-ring-color': colors.primary } as React.CSSProperties}
                />
                {hhDescriptionInfo.blocks?.socialPackageText != null && (
                  <p className="text-xs mt-1 text-gray-400">{hhDescriptionInfo.blocks.socialPackageText.length} символов</p>
                )}
                {hhDescriptionInfo.plainLength > 0 && hhDescriptionInfo.plainLength < hhDescriptionInfo.minLength && (
                  <p className="text-xs text-amber-600 mt-1">
                    Итоговое описание для HH.ru (все блоки вместе) — не менее {hhDescriptionInfo.minLength} символов (сейчас: {hhDescriptionInfo.plainLength})
                  </p>
                )}
                {hhDescriptionInfo.htmlLength > hhDescriptionInfo.maxLength && (
                  <p className="text-xs text-red-600 mt-1">
                    Итоговое описание для HH.ru (все блоки вместе, включая HTML-тэги) превышает {hhDescriptionInfo.maxLength} символов (сейчас: {hhDescriptionInfo.htmlLength}). Сократите текст.
                  </p>
                )}
                {hhDescriptionInfo.htmlLength > 0 && hhDescriptionInfo.htmlLength <= hhDescriptionInfo.maxLength && hhDescriptionInfo.plainLength >= hhDescriptionInfo.minLength && (
                  <p className="text-xs text-gray-400 mt-1">
                    Итоговое описание для HH.ru (все блоки вместе, включая HTML-тэги): {hhDescriptionInfo.htmlLength} / {hhDescriptionInfo.maxLength}
                  </p>
                )}
                {hhDescriptionInfo.warnings && hhDescriptionInfo.warnings.length > 0 && (
                  <div className="mt-1">
                    {hhDescriptionInfo.warnings.map((w, i) => (
                      <p key={i} className="text-xs text-amber-600">{w}</p>
                    ))}
                  </div>
                )}
              </div>

              {/* Industry */}
              <div data-field-id="industry">
                <label className="block text-sm font-medium text-[#0a0d14] mb-2">Отрасль</label>
                <button
                  type="button"
                  onClick={() => setIsIndustryModalOpen(true)}
                  className="w-full px-6 py-4 border border-[#e2e4e9] rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-opacity-50 text-left bg-white hover:bg-[#f6f8fa] transition-colors"
                >
                  {industryDisplayNames.length > 0 ? industryDisplayNames.join(', ') : 'Выберите отрасль...'}
                </button>
                {/* Период опыта в отрасли */}
                <div className={`mt-3 ${industry.length === 0 ? 'opacity-50' : ''}`} title={industry.length === 0 ? 'Сначала выберите отрасль' : ''}>
                  <label className="block text-sm font-medium text-[#0a0d14] mb-1">Период опыта в отрасли</label>
                  <select
                    value={experiencePeriod}
                    disabled={industry.length === 0}
                    onChange={(e) => setExperiencePeriod(e.target.value)}
                    className="w-full px-4 py-3 border border-[#e2e4e9] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 bg-white disabled:cursor-not-allowed"
                  >
                    <option value="">Любой</option>
                    {EXPERIENCE_PERIOD_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <label className={`flex items-center gap-2 mt-2 ${industry.length > 0 ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                  <input
                    type="checkbox"
                    checked={filterByExpIndustry}
                    disabled={industry.length === 0}
                    onChange={(e) => setFilterByExpIndustry(e.target.checked)}
                    className="w-4 h-4 rounded border-[#e2e4e9]"
                  />
                  <span className="text-sm">Искать по последним местам работы</span>
                </label>
              </div>

              <div className={hasAvitoIntegration ? 'grid grid-cols-1 md:grid-cols-2 gap-4' : ''}>
                {/* Специализация HH */}
                <div data-field-id="specialization">
                  <label className="block text-sm font-medium text-[#0a0d14] mb-2">
                    {hasAvitoIntegration ? 'Специализация hh' : 'Специализация'}
                  </label>
                  <button
                    type="button"
                    data-testid="vacancy-specialization-open"
                    onClick={() => setIsSpecializationModalOpen(true)}
                    className="w-full px-6 py-4 border border-[#e2e4e9] rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-opacity-50 text-left bg-white hover:bg-[#f6f8fa] transition-colors"
                  >
                    {specializationDisplayNames.length > 0 ? specializationDisplayNames.join(', ') : 'Выберите специализацию...'}
                  </button>
                  {hhVacancyId && specialization.length > 1 && (
                    <p className="text-xs text-amber-600 mt-1">HH.ru принимает только 1 специализацию, будет использована первая</p>
                  )}
                </div>

                {hasAvitoIntegration && (
                  <div data-field-id="avitoSpecialization">
                    <label className="block text-sm font-medium text-[#0a0d14] mb-2">Специализация Авито</label>
                    <button
                      type="button"
                      data-testid="vacancy-avito-specialization-open"
                      onClick={() => setIsAvitoSpecializationModalOpen(true)}
                      className="w-full px-6 py-4 border border-[#e2e4e9] rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-opacity-50 text-left bg-white hover:bg-[#f6f8fa] transition-colors"
                    >
                      {avitoSpecializationDisplayNames.length > 0 ? avitoSpecializationDisplayNames.join(', ') : 'Выберите специализацию...'}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Key Skills Section */}
            <div className="bg-white rounded-[12px] p-8 space-y-6" data-field-id="skills">
              <h2 className="text-xl font-semibold text-[#0a0d14]">Ключевые навыки</h2>
              
              <div className="space-y-6">
                {skillsInputs.map((input, index) => (
                  <div key={input.id} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => toggleInputType(input.id)}
                        className="flex items-center gap-1 text-sm font-medium transition-colors"
                        style={{ color: colors.primary }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.8'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
                      >
                        <span>{input.type === 'any' ? 'Поиск по любому из навыков' : 'Поиск по всем навыкам'}</span>
                        <ChevronDownIcon className="h-4 w-4" />
                      </button>
                      {skillsInputs.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSkillsInput(input.id)}
                          className="text-[#6b7280] hover:text-[#ef4444] transition-colors text-sm"
                        >
                          Удалить поле
                        </button>
                      )}
                    </div>
                    
                    <div className="flex gap-2 mb-1">
                      <input
                        type="text"
                        value={input.inputValue}
                        onChange={(e) => updateInputValue(input.id, e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addSkillToInput(input.id);
                          }
                        }}
                        placeholder="Добавьте навык..."
                        className="flex-1 px-6 py-4 border border-[#e2e4e9] rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-opacity-50"
                      />
                      <button
                        type="button"
                        onClick={() => addSkillToInput(input.id)}
                        style={{ backgroundColor: colors.primary }}
                        className="px-8 text-white rounded-xl font-inter transition-colors font-medium"
                      >
                        Добавить
                      </button>
                    </div>
                    <p className="text-xs text-[#868c98] mb-2">
                      Добавляйте навыки по одному или через запятую. При нажатии Enter строка автоматически разобьётся на отдельные навыки.
                    </p>
                    {input.skills.length > 0 && (
                      <div className="flex flex-wrap gap-2 items-center">
                        {input.skills.map((skill, skillIndex) => (
                          <React.Fragment key={skill}>
                            <div className="flex items-center gap-2 px-4 py-2 bg-[#f3f4f6] rounded-[9999px] text-sm">
                              <span>{skill}</span>
                              {input.type === 'all' && (
                                <>
                                  <div className="w-0 h-[14px] border-l border-[#CDD0D5]"></div>
                                  <div 
                                    className="flex items-center gap-1 cursor-not-allowed opacity-60"
                                    onClick={() => {
                                      toast('Поле опыта будет доступно в следующей версии', {
                                        icon: 'ℹ️',
                                      });
                                    }}
                                  >
                                    <span className="text-[#868C98] text-sm">Опыт</span>
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M5 7.5L10 12.5L15 7.5" stroke="#868C98" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </div>
                                </>
                              )}
                              <button
                                type="button"
                                onClick={() => removeSkillFromInput(input.id, skill)}
                                className="text-[#6b7280] hover:text-[#ef4444] transition-colors"
                              >
                                ✕
                              </button>
                            </div>
                            {skillIndex < input.skills.length - 1 && (
                              <span className="text-sm text-[#525866]">
                                {input.type === 'any' ? 'или' : 'и'}
                              </span>
                            )}
                          </React.Fragment>
                        ))}
                        {input.type === 'any' && input.skills.length > 0 && (
                          <>
                            <div className="w-0 h-9 border-l border-[#868C98]"></div>
                            <div 
                              className="flex items-center gap-1 px-4 py-2 bg-[#f3f4f6] rounded-[9999px] cursor-not-allowed opacity-60"
                              onClick={() => {
                                toast('Поле опыта будет доступно в следующей версии', {
                                  icon: 'ℹ️',
                                });
                              }}
                            >
                              <span className="text-[#868C98] text-sm">Опыт</span>
                              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5 7.5L10 12.5L15 7.5" stroke="#868C98" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    
                    <div className="box-border content-stretch flex gap-[4px] items-start px-0 py-[2px] relative shrink-0 w-full">
                      <div className="basis-0 flex flex-col font-['Inter:Regular',_sans-serif] font-normal grow justify-center leading-[0] min-h-px min-w-px not-italic relative shrink-0 text-[#525866] text-[12px]">
                        
                      </div>
                    </div>
                    
                    {index === skillsInputs.length - 1 && (
                      <>
                        <div className="border-t border-[#e2e4e9] mt-4 mb-2"></div>
                        <button
                          type="button"
                          onClick={addNewSkillsInput}
                          className="flex items-center gap-1 text-sm font-medium transition-colors"
                          style={{ color: colors.primary }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.8'; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ color: colors.primary }}>
                            <path d="M8 4V12M4 8H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                          <span>Добавить еще поле</span>
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Experience Section */}
            <div className="bg-white rounded-[12px] p-8 space-y-6" data-field-id="experience">
              <h2 className="text-xl font-semibold text-[#0a0d14]">Опыт работы</h2>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[#0a0d14] mb-2">
                    Опыт в должности <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-3">
                    {EXPERIENCE_OPTIONS.map((exp) => (
                      <label key={exp} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid={`vacancy-position-experience-${exp}`}
                          checked={experienceInPosition.includes(exp)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setExperienceInPosition([...experienceInPosition, exp]);
                            } else {
                              setExperienceInPosition(experienceInPosition.filter(item => item !== exp));
                            }
                          }}
                          className="w-4 h-4 rounded border-[#e2e4e9]"
                        />
                        <span className="text-sm">{exp}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0a0d14] mb-2">Общий опыт</label>
                  <div className="space-y-3">
                    {EXPERIENCE_OPTIONS.map((exp) => (
                      <label key={exp} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid={`vacancy-total-experience-${exp}`}
                          checked={totalExperience.includes(exp)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setTotalExperience([...totalExperience, exp]);
                            } else {
                              setTotalExperience(totalExperience.filter(item => item !== exp));
                            }
                          }}
                          className="w-4 h-4 rounded border-[#e2e4e9]"
                        />
                        <span className="text-sm">{exp}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <ExcludeJumpersCheckbox checked={excludeJumpers} onChange={setExcludeJumpers} />
            </div>

            {/* Conditions Section */}
            <div className="bg-white rounded-[12px] p-8 space-y-6" data-field-id="salary">
              <h2 className="text-xl font-semibold text-[#0a0d14]">Условия</h2>

              {/* Salary */}
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <label className="text-sm font-medium text-[#0a0d14]">
                    Зарплатные ожидания <span className="text-red-500">*</span>
                  </label>
                  <select
                    data-testid="vacancy-salary-currency"
                    value={salaryCurrency}
                    onChange={(e) => setSalaryCurrency(e.target.value)}
                    className="px-2 py-1 border border-[#e2e4e9] rounded-lg text-sm focus:outline-none bg-white"
                    title="Валюта зарплаты"
                  >
                    {hhCurrencies.length > 0
                      ? hhCurrencies.map((c) => (
                          <option key={c.id} value={c.id}>{c.abbr || c.id} — {c.name}</option>
                        ))
                      : (
                          <>
                            <option value="RUR">₽ — Рубли</option>
                            <option value="USD">$ — Доллары</option>
                            <option value="EUR">€ — Евро</option>
                            <option value="BYR">Br — Белорусские рубли</option>
                            <option value="KZT">₸ — Тенге</option>
                          </>
                        )
                    }
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formatNumberWithSpaces(salaryFrom)}
                    onChange={(e) => {
                      const rawValue = parseFormattedNumber(e.target.value);
                      if (rawValue === '' || /^\d+$/.test(rawValue)) {
                        setSalaryFrom(rawValue);
                        if (validationErrors.salary) {
                          setValidationErrors(prev => ({ ...prev, salary: false }));
                        }
                      }
                    }}
                    placeholder="от"
                    className={`px-6 py-4 border rounded-xl text-base focus:outline-none ${
                      validationErrors.salary ? 'border-red-500 bg-red-50' : 'border-[#e2e4e9]'
                    }`}
                  />
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formatNumberWithSpaces(salaryTo)}
                    onChange={(e) => {
                      const rawValue = parseFormattedNumber(e.target.value);
                      if (rawValue === '' || /^\d+$/.test(rawValue)) {
                        setSalaryTo(rawValue);
                        if (validationErrors.salary) {
                          setValidationErrors(prev => ({ ...prev, salary: false }));
                        }
                      }
                    }}
                    placeholder="до *"
                    className={`px-6 py-4 border rounded-xl text-base focus:outline-none ${
                      validationErrors.salary ? 'border-red-500 bg-red-50' : 'border-[#e2e4e9]'
                    }`}
                  />
                </div>
                <div className="flex flex-wrap gap-6 mt-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="vacancy-salary-specified"
                      checked={salarySpecified}
                      onChange={(e) => setSalarySpecified(e.target.checked)}
                      className="w-4 h-4 rounded border-[#e2e4e9]"
                    />
                    <span className="text-sm">Указана ЗП</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="vacancy-salary-gross"
                      checked={salaryGross === true}
                      onChange={(e) => setSalaryGross(e.target.checked)}
                      className="w-4 h-4 rounded border-[#e2e4e9]"
                    />
                    <span className="text-sm">ЗП до вычета налога (gross)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="vacancy-salary-nda"
                      checked={hasNda}
                      onChange={(e) => setHasNda(e.target.checked)}
                      className="w-4 h-4 rounded border-[#e2e4e9]"
                    />
                    <span className="text-sm">NDA</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="vacancy-salary-bonuses"
                      checked={hasBonuses}
                      onChange={(e) => setHasBonuses(e.target.checked)}
                      className="w-4 h-4 rounded border-[#e2e4e9]"
                    />
                    <span className="text-sm">Премии</span>
                  </label>
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-[#0a0d14] mb-2">Частота выплат</label>
                  <select
                    data-testid="vacancy-salary-frequency"
                    value={salaryFrequency}
                    onChange={(e) => setSalaryFrequency(e.target.value)}
                    className="w-full px-6 py-4 border border-[#e2e4e9] rounded-xl text-base focus:outline-none"
                  >
                    <option value="">Не указано</option>
                    <option value="MONTHLY">Раз в месяц</option>
                    <option value="TWICE_A_MONTH">Два раза в месяц</option>
                    <option value="WEEKLY">Раз в неделю</option>
                  </select>
                </div>
              </div>

              {/* Education */}
              <div>
                <label className="block text-sm font-medium text-[#0a0d14] mb-2">Образование</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {EDUCATION_OPTIONS.filter((e) => e.value !== '').map((edu) => {
                    const current = Array.isArray(education) ? education : [];
                    const checked = current.includes(edu.value);
                    return (
                      <label key={edu.label} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid={`vacancy-education-${edu.value}`}
                          checked={checked}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? Array.from(new Set([...current, edu.value]))
                              : current.filter((v) => v !== edu.value);
                            setEducation(next);
                          }}
                          className="w-4 h-4 rounded border-[#e2e4e9]"
                        />
                        <span className="text-sm">{edu.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Geography and Profile Section */}
            <div className="bg-white rounded-[12px] p-8 space-y-6" data-field-id="region">
              <h2 className="text-xl font-semibold text-[#0a0d14]">География и профиль</h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-[#0a0d14] mb-2">География</label>
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={() => setIsAreaModalOpen(true)}
                      className="w-full px-6 py-4 border border-[#e2e4e9] rounded-xl text-base text-left focus:outline-none focus:ring-2 focus:ring-opacity-50 bg-white hover:bg-[#f6f8fa] transition-colors"
                    >
                      {selectedCities.length > 0 ? selectedCities.join(', ') : 'Выберите города...'}
                    </button>
                    <label className={`flex items-center gap-2 ${selectedCities.length === 0 ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
                      <input
                        type="checkbox"
                        data-testid="vacancy-strict-region-search"
                        checked={strictRegionSearch}
                        disabled={selectedCities.length === 0}
                        onChange={(e) => {
                          setStrictRegionSearch(e.target.checked);
                        }}
                        className="w-4 h-4 rounded border-[#e2e4e9]"
                      />
                      <span className="text-sm text-[#0a0d14]">Живет в указанном регионе</span>
                    </label>
                  </div>
                </div>

                {/* Age */}
                <div>
                  <label className="block text-sm font-medium text-[#0a0d14] mb-2">Возраст</label>
                  <div className="grid grid-cols-2 gap-4 mb-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={ageFrom}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d+$/.test(value)) {
                          const numValue = value === '' ? null : parseInt(value, 10);
                          if (value === '' || (numValue !== null && numValue >= 0 && numValue <= 100)) {
                            setAgeFrom(value);
                          }
                        }
                      }}
                      placeholder="от"
                      className="px-6 py-4 border border-[#e2e4e9] rounded-xl text-base focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                    <input
                      type="text"
                      inputMode="numeric"
                      value={ageTo}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d+$/.test(value)) {
                          const numValue = value === '' ? null : parseInt(value, 10);
                          if (value === '' || (numValue !== null && numValue >= 0 && numValue <= 100)) {
                            setAgeTo(value);
                          }
                        }
                      }}
                      placeholder="до"
                      className="px-6 py-4 border border-[#e2e4e9] rounded-xl text-base focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="vacancy-include-no-age"
                      checked={includeNoAge}
                      onChange={(e) => setIncludeNoAge(e.target.checked)}
                      className="w-4 h-4 rounded border-[#e2e4e9]"
                    />
                    <span className="text-sm">Включая без возраста</span>
                  </label>
                </div>

                {/* Gender */}
                <div>
                  <label className="block text-sm font-medium text-[#0a0d14] mb-2">Пол</label>
                  <div className="flex gap-6">
                    {GENDER_OPTIONS.map((option) => (
                      <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="gender"
                          data-testid={`vacancy-gender-${option.value}`}
                          value={option.value}
                          checked={gender === option.value}
                          onChange={(e) => setGender(e.target.value)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0a0d14] mb-2">Гражданство</label>
                  <button
                    type="button"
                    onClick={() => setIsCitizenshipModalOpen(true)}
                    className="w-full px-6 py-4 border border-[#e2e4e9] rounded-xl text-base text-left focus:outline-none focus:ring-2 focus:ring-opacity-50 bg-white hover:bg-[#f6f8fa] transition-colors flex items-center justify-between"
                  >
                    <span className={citizenship.length > 0 ? 'text-[#0a0d14]' : 'text-gray-400'}>
                      {selectedCitizenshipNames || 'Выберите гражданство...'}
                    </span>
                    <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                  </button>
                </div>
              </div>
            </div>

            {/* Work Format Section */}
            <div className="bg-white rounded-[12px] p-8 space-y-6" data-field-id="workFormat">
              <h2 className="text-xl font-semibold text-[#0a0d14]">
                Формат работы <span className="text-red-500">*</span>
              </h2>

              <div>
                <label className="block text-sm font-medium text-[#0a0d14] mb-2">Офис, Гибрид</label>
                <select
                  data-testid="vacancy-work-format"
                  value={workFormat}
                  onChange={(e) => setWorkFormat(e.target.value)}
                  className="w-full px-6 py-4 border border-[#e2e4e9] rounded-xl text-base focus:outline-none"
                >
                  <option value="">Выберите формат работы</option>
                  {WORK_FORMATS.map((format) => (
                    <option key={format} value={format}>
                      {format}
                    </option>
                  ))}
                </select>
              </div>

              {(workFormat === 'Офис' || workFormat === 'Гибрид') && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0a0d14] mb-2">Город</label>
                    <button
                      type="button"
                      onClick={() => setIsOfficeCityModalOpen(true)}
                      className="w-full px-6 py-4 border border-[#e2e4e9] rounded-xl text-base text-left focus:outline-none focus:ring-2 focus:ring-opacity-50 bg-white hover:bg-[#f6f8fa] transition-colors"
                    >
                      {officeCityName || 'Выберите город...'}
                    </button>
                  </div>

                  {officeCityId && (() => {
                    const cityLines = metroCities.find((c) => String(c.id) === String(officeCityId))?.lines || [];
                    const hasMetro = cityLines.some(l => l.stations && l.stations.length > 0);
                    return (
                      <div className="space-y-3">
                        {metroEntries.map((entry, idx) => (
                          <div key={idx} className="space-y-3">
                            <div className="flex gap-3 items-start">
                              <div className="flex-1 space-y-3">
                                <div>
                                  <label className="block text-sm font-medium text-[#0a0d14] mb-2">Адрес</label>
                                  <input
                                    type="text"
                                    value={entry.address}
                                    onChange={(e) => {
                                      const updated = [...metroEntries];
                                      updated[idx] = {
                                        ...updated[idx],
                                        address: e.target.value,
                                        hhAddressId: undefined,
                                      };
                                      setMetroEntries(updated);
                                    }}
                                    className="w-full px-6 py-4 border border-[#e2e4e9] rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-opacity-50"
                                    placeholder="Укажите адрес"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-[#0a0d14] mb-2">Адрес HH для экспорта</label>
                                  <select
                                    value={entry.hhAddressId || ''}
                                    onChange={(e) => {
                                      const selectedId = e.target.value;
                                      const selectedAddress = hhEmployerAddresses.find(address => address.id === selectedId);
                                      const updated = [...metroEntries];
                                      updated[idx] = {
                                        ...updated[idx],
                                        hhAddressId: selectedAddress?.id || undefined,
                                        address: selectedAddress?.raw || updated[idx].address,
                                        stationId: selectedAddress?.metroStations?.[0]?.stationId || updated[idx].stationId,
                                      };
                                      setMetroEntries(updated);
                                    }}
                                    disabled={hhEmployerAddressesLoading}
                                    className="w-full px-6 py-4 border border-[#e2e4e9] rounded-xl text-base focus:outline-none disabled:bg-gray-100"
                                  >
                                    <option value="">
                                      {hhEmployerAddressesLoading ? 'Загрузка адресов HH...' : 'Не выбран'}
                                    </option>
                                    {entry.hhAddressId && !hhEmployerAddresses.some(address => address.id === entry.hhAddressId) && (
                                      <option value={entry.hhAddressId}>
                                        {entry.address || entry.hhAddressId}
                                      </option>
                                    )}
                                    {hhEmployerAddresses.map(address => (
                                      <option key={address.id} value={address.id}>
                                        {address.raw || address.name || address.description || address.id}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                              {metroEntries.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => setMetroEntries(metroEntries.filter((_, i) => i !== idx))}
                                  className="mt-8 text-[#868c98] hover:text-red-500 transition-colors"
                                  title="Удалить"
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                            {hasMetro && (
                              <div>
                                <label className="block text-sm font-medium text-[#0a0d14] mb-2">Ближайшее метро</label>
                                <SearchableMetroSelect
                                  value={entry.stationId}
                                  onChange={(stationId) => {
                                    const updated = [...metroEntries];
                                    updated[idx] = { ...updated[idx], stationId: Array.isArray(stationId) ? stationId[0] || '' : stationId };
                                    setMetroEntries(updated);
                                  }}
                                  lines={cityLines}
                                />
                              </div>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => setMetroEntries([...metroEntries, {stationId: '', address: ''}])}
                          className="text-sm text-[#f27935] hover:underline"
                        >
                          {hasMetro ? '+ Добавить адрес или метро' : '+ Добавить адрес'}
                        </button>
                        {hasMetro && (
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={useMetroInSearch}
                              onChange={(e) => setUseMetroInSearch(e.target.checked)}
                              className="w-4 h-4 rounded border-[#e2e4e9]"
                            />
                            <span className="text-sm text-[#525866]">Учитывать в поиске станцию метро</span>
                          </label>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-[#0a0d14] mb-2">График работы</label>
                <select
                  data-testid="vacancy-work-schedule"
                  value={workSchedule}
                  onChange={(e) => setWorkSchedule(e.target.value)}
                  className="w-full px-6 py-4 border border-[#e2e4e9] rounded-xl text-base focus:outline-none"
                >
                  <option value="">Выберите график работы</option>
                  {workSchedule
                    && !hhWorkSchedules.some((s) => s.name === workSchedule)
                    && !WORK_SCHEDULES.some((schedule) => schedule === workSchedule) && (
                      <option value={workSchedule}>{workSchedule}</option>
                    )}
                  {hhWorkSchedules.length > 0
                    ? hhWorkSchedules.map((s) => (
                        <option key={s.id} value={s.name}>
                          {s.name}
                        </option>
                      ))
                    : WORK_SCHEDULES.map((schedule) => (
                        <option key={schedule} value={schedule}>
                          {schedule}
                        </option>
                      ))
                  }
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#0a0d14] mb-2">Рабочие часы в сутки</label>
                <select
                  data-testid="vacancy-working-hours"
                  value={workingHours}
                  onChange={(e) => setWorkingHours(e.target.value)}
                  className="w-full px-6 py-4 border border-[#e2e4e9] rounded-xl text-base focus:outline-none"
                >
                  <option value="">Не указано</option>
                  <option value="HOURS_4">4 часа</option>
                  <option value="HOURS_6">6 часов</option>
                  <option value="HOURS_8">8 часов</option>
                  <option value="HOURS_10">10 часов</option>
                  <option value="HOURS_12">12 часов</option>
                </select>
              </div>
            </div>

            {/* Relocation Section */}
            <div className="bg-white rounded-[12px] p-8 space-y-6" data-field-id="relocation">
              <h2 className="text-xl font-semibold text-[#0a0d14]">Релокация</h2>

              <div>
                <label className="block text-sm font-medium text-[#0a0d14] mb-2">Страна или город релокации</label>
                <input
                  type="text"
                  data-testid="vacancy-relocation-location"
                  value={relocationLocation}
                  onChange={(e) => setRelocationLocation(e.target.value)}
                  placeholder="Введите город или страну"
                  className="w-full px-6 py-4 border border-[#e2e4e9] rounded-xl text-base focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#0a0d14] mb-2">Что даёт компания для помощи при релокации?</label>
                <input
                  type="text"
                  data-testid="vacancy-relocation-assistance"
                  value={relocationAssistance}
                  onChange={(e) => setRelocationAssistance(e.target.value)}
                  placeholder="Введите варианты помощи при релокации"
                  className="w-full px-6 py-4 border border-[#e2e4e9] rounded-xl text-base focus:outline-none"
                />
              </div>
            </div>

            {/* Candidate Status and Employment Contract Types in one row */}
            <div className="grid grid-cols-2 gap-8">
              {/* Candidate Status Section */}
              <div className="bg-white rounded-[12px] p-8 space-y-6" data-field-id="candidateStatus">
                <h2 className="text-xl font-semibold text-[#0a0d14]">Статус поиска</h2>

                <div className="space-y-3">
                  {CANDIDATE_STATUSES.map((status) => (
                    <label key={status.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid={`vacancy-candidate-status-${status.value}`}
                        checked={candidateStatuses.includes(status.value)}
                        onChange={() => toggleCandidateStatus(status.value)}
                        className="w-4 h-4 rounded border-[#e2e4e9]"
                      />
                      <span className="text-sm">{status.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Employment Contract Types Section */}
              <div className="bg-white rounded-[12px] p-8 space-y-6" data-field-id="employmentContract">
                <h2 className="text-xl font-semibold text-[#0a0d14]">Оформление</h2>

                <div className="space-y-2">
                  {EMPLOYMENT_CONTRACT_TYPES.map((type) => (
                    <label key={type.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid={`vacancy-employment-contract-${type.value}`}
                        checked={employmentContractTypes.includes(type.value)}
                        onChange={() => toggleContractType(type.value)}
                        className="w-4 h-4 rounded border-[#e2e4e9]"
                      />
                      <span className="text-sm">{type.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

          </div>
          </div>
          </div>{/* end Container A */}

          {/* Container B: Collapsed AI sidebar */}
          {showCollapsedAiSidebar && onExpandAiPanel && (
            <div
              className="shrink-0 sticky top-0 flex flex-col items-center px-5 ml-5 mt-[36px] rounded-[16px] cursor-pointer select-none ai-sidebar-in"
              style={{ backgroundColor: 'rgba(215, 105, 88, 0.4)', height: `calc(100dvh - ${aiPanelOffset}px - 20px)` }}
              onClick={onExpandAiPanel}
            >
              <div className="mt-4 w-10 h-10 rounded-[10px] flex items-center justify-center bg-white shadow-sm shrink-0">
                <img src={aiAssistantIcon} alt="AI" className="w-[18px] h-[18px]" />
              </div>
              <div className="flex-1 flex items-center justify-center">
                <span
                  className="text-[12px] font-semibold tracking-[0.1em] text-[#0a0d14] whitespace-nowrap"
                  style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
                >
                  AI АССИСТЕНТ
                </span>
              </div>
              <div className="mb-4 w-9 h-9 rounded-[10px] flex items-center justify-center bg-white shadow-sm shrink-0">
                <img src={aiPanelExpandIcon} alt="Expand" className="w-[10px] h-[10px]" />
              </div>
            </div>
          )}

          {/* Expanded AI panel */}
          {rightPanel && (
            <div
              ref={aiPanelRef}
              className="shrink-0 sticky top-[72px] z-[35] ml-5 mt-[36px] rounded-[16px] overflow-hidden ai-slide-in-right"
            >
              {rightPanel}
            </div>
          )}

          {/* Internal requirements panel (edit mode). Hidden on dialog/interview settings tabs —
              requirements are managed in a simpler view inside those tabs (no weights, no splits). */}
          {!rightPanel && vacancyId && showInternalReqPanel && internalRequirements && activeTab === 'vacancy' && (
            <div
              ref={reqPanelCallbackRef}
              className="shrink-0 sticky top-[72px] z-[35] ml-5 mt-[36px] rounded-[16px] overflow-hidden ai-slide-in-right flex flex-col"
              style={{ maxHeight: 'calc(100vh - 100px)' }}
            >
              <RequirementsPanel
                required={internalRequirements.required}
                optional={internalRequirements.optional}
                onUpdate={(req, opt) => {
                  requirementsEditedInVacancyFormRef.current = true;
                  setInternalRequirements({ required: req, optional: opt });
                }}
                onCollapse={() => setShowInternalReqPanel(false)}
                onRegenerateWithAi={async () => {
                  try {
                    const newJson = await apiService.regeneratePositionAiRequirements(vacancyId);
                    const parsed = JSON.parse(newJson);
                    const next = {
                      required: (parsed.required || []).map((r: any, i: number) => ({ id: `req-${i}`, text: r.text, weight: r.weight ?? 7 })),
                      optional: (parsed.optional || []).map((r: any, i: number) => ({ id: `opt-${i}`, text: r.text, weight: r.weight ?? 3 })),
                    };
                    requirementsEditedInVacancyFormRef.current = true;
                    setInternalRequirements(next);
                    return next;
                  } catch (e) {
                    console.error('Failed to regenerate AI requirements:', e);
                    return null;
                  }
                }}
              />
            </div>
          )}

          {/* Collapsed requirements panel (edit mode) — hidden outside the vacancy tab. */}
          {!rightPanel && !showCollapsedAiSidebar && vacancyId && !showInternalReqPanel && internalRequirements && activeTab === 'vacancy' && (
            <div
              ref={reqBookmarkCallbackRef}
              className="shrink-0 sticky top-0 flex flex-col items-center px-5 ml-5 mt-[36px] rounded-[16px] cursor-pointer select-none ai-sidebar-in"
              style={{ backgroundColor: 'rgba(55, 93, 251, 0.15)' }}
              onClick={() => setShowInternalReqPanel(true)}
            >
              <div className="mt-4 w-10 h-10 rounded-[10px] flex items-center justify-center bg-white shadow-sm shrink-0">
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M10 3L2 7.5l8 4.5 8-4.5L10 3z" stroke="#375DFB" strokeWidth="1.4" strokeLinejoin="round"/><path d="M5 9.5v4.5c0 1.5 2.2 3 5 3s5-1.5 5-3V9.5" stroke="#375DFB" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <div className="flex-1 flex items-center justify-center">
                <span
                  className="text-[12px] font-semibold tracking-[0.1em] text-[#0a0d14] whitespace-nowrap"
                  style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
                >
                  ТРЕБОВАНИЯ
                </span>
              </div>
              <div className="mb-4 w-9 h-9 rounded-[10px] flex items-center justify-center bg-white shadow-sm shrink-0">
                <img src={aiPanelExpandIcon} alt="Expand" className="w-[10px] h-[10px]" />
              </div>
            </div>
          )}
        </div>{/* end flex container A+B */}
      </div>

      <IndustryModal
        isOpen={isIndustryModalOpen}
        onClose={() => setIsIndustryModalOpen(false)}
        selectedIndustries={industry}
        filterByExpIndustry={filterByExpIndustry}
        experiencePeriod={experiencePeriod}
        onSelectionChange={(industries, filterByExpIndustryValue, expPeriod) => {
          handleIndustrySelectionChange(industries, filterByExpIndustryValue);
          if (expPeriod !== undefined) setExperiencePeriod(expPeriod);
        }}
      />

      <SpecializationModal
        isOpen={isSpecializationModalOpen}
        onClose={() => setIsSpecializationModalOpen(false)}
        selectedSpecializations={specialization}
        selectionMode="multi"
        onSelectionChange={handleSpecializationSelectionChange}
      />
      <SpecializationModal
        isOpen={isAvitoSpecializationModalOpen}
        onClose={() => setIsAvitoSpecializationModalOpen(false)}
        selectedSpecializations={avitoSpecialization}
        selectionMode="multi"
        onSelectionChange={handleAvitoSpecializationSelectionChange}
        fetchData={fetchAvitoResumeSpecializations}
        mode="flat"
        title="Специализация Авито"
      />
      <AreaModal
        isOpen={isAreaModalOpen}
        onClose={() => setIsAreaModalOpen(false)}
        selectedAreas={selectedAvitoLocationIds}
        onApply={handleAvitoAreaSelectionChange}
        fetchData={fetchAvitoLocations}
        hideCountryTabs
      />
      <AreaModal
        isOpen={isHhAreaFallbackModalOpen}
        onClose={() => setIsHhAreaFallbackModalOpen(false)}
        selectedAreas={selectedCityIds}
        onApply={handleHhAreaFallbackSelectionChange}
        title="Выберите географию hh.ru"
        subtitle={hhAreaFallbackSubtitle}
      />
      <AreaModal
        isOpen={isOfficeCityModalOpen}
        onClose={() => setIsOfficeCityModalOpen(false)}
        selectedAreas={officeCityId ? [officeCityId] : []}
        onApply={(areas) => {
          const newCityId = areas[0] || '';
          setOfficeCityId(newCityId);
          if (newCityId) {
            getAreaNameById(newCityId).then(name => setOfficeCityName(name || ''));
          } else {
            setOfficeCityName('');
          }
          const newCityLines = metroCities.find((c) => String(c.id) === String(newCityId))?.lines || [];
          const newCityHasMetro = newCityLines.some(l => l.stations && l.stations.length > 0);
          setMetroEntries([{stationId: '', address: ''}]);
          if (!newCityHasMetro) {
            setUseMetroInSearch(false);
          }
        }}
        singleSelect
        title="Выберите город"
      />
      <SearchMultiSelectModal
        isOpen={isCitizenshipModalOpen}
        onClose={() => setIsCitizenshipModalOpen(false)}
        title="Гражданство"
        items={countries}
        selectedItems={citizenship}
        onSelect={setCitizenship}
        searchPlaceholder="Поиск страны..."
      />

      {/* Экспорт-флоу: модалка выбора специализации для публикации */}
      <SpecializationModal
        isOpen={isExportSpecModalOpen}
        onClose={() => setIsExportSpecModalOpen(false)}
        selectedSpecializations={specialization}
        selectionMode="single_category"
        onSelectionChange={handleExportSpecSelected}
        title="Выберите специализацию для публикации"
        subtitle="Для публикации на HH.ru необходимо выбрать одну специализацию"
      />

      {/* Экспорт-флоу: модалка выбора города для публикации */}
      <AreaModal
        isOpen={isExportAreaModalOpen}
        onClose={() => setIsExportAreaModalOpen(false)}
        selectedAreas={selectedCityIds}
        onApply={handleExportCitySelected}
        singleSelect
        title="Выберите город публикации"
        subtitle="Каждый город — это отдельная публикация"
      />

      {/* Экспорт-флоу: диалог подтверждения публикации */}
      {isExportConfirmOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-[#0a0d14] mb-4">{hhVacancyId ? 'Подтверждение обновления' : 'Подтверждение публикации'}</h3>
            <p className="text-sm text-[#525866] mb-6">
              {hhVacancyId ? 'Вакансия будет обновлена на hh.ru. Вы хотите продолжить?' : 'Вакансия будет отправлена на публикацию на hh.ru. Вы хотите продолжить?'}
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsExportConfirmOpen(false)}
                className="px-5 py-2.5 rounded-xl text-sm font-medium border border-[#e2e4e9] text-[#525866] hover:bg-[#f6f8fa] transition-colors"
              >
                Нет
              </button>
              <button
                type="button"
                onClick={handleExportConfirm}
                className="px-5 py-2.5 rounded-xl text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
              >
                Да
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
}
